/* ActivityPanel — iter-347 · Updates Timeline
 * ═══════════════════════════════════════════════════════════════════
 * Single chronological timeline that IS the source of truth for
 * every transaction event: emails, messages, document uploads +
 * requests, milestone updates, internal notes (formerly the
 * standalone Operational Notes panel), system events.
 *
 * iter-347 refinement:
 *   • User-visible label is now "Updates" — entries read as
 *     verb-first actions ("Uploaded…", "Marked…", "Added
 *     internal note") instead of category labels.
 *   • Operational Notes are ingested here as `internal_note`
 *     events with a lock affordance — the timeline is the single
 *     source of truth for all transaction activity.
 *   • Internal identifiers (kind ids · testids · filter ids) are
 *     preserved so contract tests + deep-link URLs stay stable.
 *
 * Aggregates data from four sources so the operator scans a
 * single continuous stream:
 *
 *   1. Manual logs (composer + persisted to localStorage)
 *   2. Document uploads / requests (from `documents` prop)
 *   3. Milestone updates (from `milestones.items`)
 *   4. Operational / internal notes (from `operationalNotes` prop)
 *
 * Architecture ready for future plug-ins:
 *   • Email Integration → `kind: "email"` (route source)
 *   • Automations → `kind: "system"` (rule engine)
 *   • Risk Intelligence → derived filter overlay
 *   • Reporting → same event feed → aggregation
 *   • AI Summaries → summarise the same stream
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  Mail, MessageCircle, Phone, MessageSquare, StickyNote,
  MoreHorizontal, X, Paperclip, FileText, FilePlus2,
  CheckCircle2, Zap, Lock, Eye, PhoneCall,
  Users, ChevronRight, ChevronDown, ChevronLeft, Clock as ClockIcon, Check,
  Target, AlertOctagon, Milestone as MilestoneIcon, Sparkles, ClipboardList,
  Plus, RefreshCw, Hourglass,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { api } from "../../lib/api";
import { dealStatusFor } from "../../data/dealHealth";
import { DemoBadge } from "../ui/DemoBadge";
import { Switch } from "../ui/switch";
import { useAuth } from "../../data/authStore";
import { ActivityGroupRow } from "./ActivityGroupRow";
import { prettyStage } from "./OperationalEventSheet";
import { actorFor, buildTimeline, compareEvents, contextLine, humanizeRoleKeys, isMilestoneEvent, milestoneTitle, recordedByLine, ukDayKey, ukDayLabel, ukTime } from "./activityFeedModel";

/* ─── Kind registry ──────────────────────────────────────────────
 * Canonical event kinds. `Icon` renders in the timeline
 * avatar tile; `tint` powers the tone across icon + subtle chip.
 * `label` is the verb-first action-led title rendered in the row
 * header — iter-347 rewrote every label so entries read as
 * actions taken ("Uploaded document") rather than category
 * names ("Document uploaded"). Every future integration extends
 * this registry rather than inventing a new row shape. */
export const ACTIVITY_KINDS = {
  email:            { label: "Sent email",              Icon: Mail,          category: "communication", tint: "text-sky-600",     ring: "ring-sky-200/70",     bg: "bg-sky-50"     },
  message:          { label: "Sent message",            Icon: MessageCircle, category: "communication", tint: "text-emerald-600", ring: "ring-emerald-200/70", bg: "bg-emerald-50" },
  phone:            { label: "Logged phone call",       Icon: Phone,         category: "communication", tint: "text-violet-600",  ring: "ring-violet-200/70",  bg: "bg-violet-50"  },
  sms:              { label: "Sent SMS",                Icon: MessageSquare, category: "communication", tint: "text-amber-600",   ring: "ring-amber-200/70",   bg: "bg-amber-50"   },
  note:             { label: "Added note",              Icon: StickyNote,    category: "notes",         tint: "text-slate-600",   ring: "ring-slate-200/70",   bg: "bg-slate-50"   },
  internal_note:    { label: "Added internal note",     Icon: Lock,          category: "notes",         tint: "text-slate-700",   ring: "ring-slate-200/70",   bg: "bg-slate-50"   },
  chase:            { label: "Chased counterparty",     Icon: PhoneCall,     category: "communication", tint: "text-violet-600",  ring: "ring-violet-200/70",  bg: "bg-violet-50"  },
  document_upload:  { label: "Document received",      Icon: FileText,      category: "documents",     tint: "text-indigo-600",  ring: "ring-indigo-200/70",  bg: "bg-indigo-50"  },
  document_request: { label: "Document requested",     Icon: FilePlus2,     category: "documents",     tint: "text-amber-700",   ring: "ring-amber-200/70",   bg: "bg-amber-50"   },
  milestone:        { label: "Marked milestone complete", Icon: CheckCircle2, category: "milestones",   tint: "text-emerald-700", ring: "ring-emerald-200/70", bg: "bg-emerald-50" },
  system:           { label: "System event",            Icon: Zap,           category: "system",        tint: "text-slate-500",   ring: "ring-slate-200/70",   bg: "bg-slate-50"   },
};

// Titles for manually logged entries — never imply ChainFlow sent them.
const MANUAL_LOG_TITLES = {
  email: "Email logged", message: "WhatsApp logged", phone: "Call logged",
  sms: "SMS logged", note: "Note added", internal_note: "Internal note added",
};
const _GENERIC_SENT_TITLES = new Set(["Sent email", "Sent message", "Sent SMS"]);
// Legacy manual logs stored a generic "Sent …" title; show them as "… logged".
const _canonTitle = (a, kind, manual) =>
  (_GENERIC_SENT_TITLES.has(a.title) && (manual || a.activity_type === "message") && MANUAL_LOG_TITLES[kind]) || a.title;

// Presentation only: stage-change titles read with the canonical stage labels, not raw stage keys.
export function readableStageTitle(e) {
  const raw = e?.meta?.raw || {};
  if (e?.meta?.activityType !== "stage_change" || !raw.to) return e?.title;
  return `Stage advanced · ${raw.from ? `${prettyStage(raw.from)} → ` : ""}${prettyStage(raw.to)}`;
}

// Presentation only: UK display dates for raw ISO dates in titles; invalid text is left untouched.
const _ISO_DAY_RE = /\b(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?(?!\d)/g;
export function ukDatesInText(text) {
  if (typeof text !== "string" || !text) return text;
  return text.replace(_ISO_DAY_RE, (match, y, mo, d) => {
    const dt = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
    if (dt.getUTCFullYear() !== Number(y) || dt.getUTCMonth() !== Number(mo) - 1 || dt.getUTCDate() !== Number(d)) return match;
    return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
  });
}

// An auto "… blocker cleared" note is no longer current once its milestone was reopened.
export function decorateDisplayEvents(events, activities, milestones) {
  const reopenedAt = {};
  (activities || []).forEach((a) => {
    const mid = a?.activity_type === "milestone_reopened" ? a?.meta?.milestone_id : null;
    const t = mid ? Date.parse(a.created_at) : NaN;
    if (mid && t > (reopenedAt[mid] || 0)) reopenedAt[mid] = t;
  });
  const pending = new Set(((milestones && milestones.items) || [])
    .filter((m) => m && m.state !== "done" && !m.completed_at).map((m) => m.id));
  return (events || []).map((e) => {
    const m = e?.meta?.raw || {};
    const superseded = e?.meta?.activityType === "risk_cleared" && !!m.auto && !!m.via_milestone
      && ((reopenedAt[m.via_milestone] || 0) > (e.atMs || 0) || pending.has(m.via_milestone));
    // A completion later reopened is history, not the latest material change.
    const staleCompletion = e?.meta?.activityType === "milestone_completed" && !!m.milestone_id
      && ((reopenedAt[m.milestone_id] || 0) > (e.atMs || 0) || pending.has(m.milestone_id));
    const title = ukDatesInText(e.title);
    if (title === e.title && !superseded && !staleCompletion) return e;
    // Both the auto "blocker cleared" note and the completion itself read as no longer current.
    return { ...e, title, superseded: superseded || staleCompletion, staleCompletion };
  });
}

const SHOW_SYSTEM_KEY = "chainflow:deal-activity:show-system";
const FILTER_TABS = [
  // iter-alignment · P0 · Activity Feed (approved pilot version) —
  // (2026-02-28) filters trimmed to All · Communication · Documents ·
  // Notes.  Milestones and System were retired as primary filters;
  // meaningful milestone/system events still surface within the
  // "All" stream via the existing aggregator, they just no longer
  // get a dedicated top-level chip.
  { id: "all",           label: "All",           categories: null                       },
  { id: "communication", label: "Communication", categories: ["communication"]          },
  { id: "milestones",    label: "Milestones",    categories: ["milestones"]             },
  { id: "documents",     label: "Documents",     categories: ["documents"]              },
  { id: "notes",         label: "Notes",         categories: ["notes"]                  },
];

/* ─── Storage — legacy iter-343 bucket preserved so historical
 * updates remain visible without a migration step. */
const storageKey = (dealId) => `chainflow:deal-updates:${dealId}`;
/* iter-357 — Operational persistence layer.
 *
 * Two additional per-deal / per-user storage surfaces power the
 * finalised Communication Workspace:
 *
 *   • `chainflow:deal-chases:<dealId>`  → list of chase events
 *     appended by the Chase quick-action. Each entry survives
 *     reloads, participates in the aggregator as a `chase` event,
 *     and is used to derive the freshest `lastChaseAtMs` per
 *     waiting-on entry.
 *   • `chainflow:last-visit:<userId>:<dealId>` → the epoch of the
 *     current user's most recent visit to the Communication tab.
 *     Per-user so shared workspaces surface only THIS operator's
 *     "since last visit" deltas.
 *
 * All three helpers no-op safely under SSR / private mode so the
 * page still renders on hosts without localStorage.
 */
const chaseStorageKey = (dealId) => `chainflow:deal-chases:${dealId}`;
const lastVisitStorageKey = (userId, dealId) =>
  `chainflow:last-visit:${userId || "anon"}:${dealId}`;

export function loadUpdates(dealId) {
  if (!dealId || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(storageKey(dealId));
    return raw ? (JSON.parse(raw) || []) : [];
  } catch { return []; }
}

function persistUpdates(dealId, list) {
  if (!dealId || typeof window === "undefined") return;
  try { window.localStorage.setItem(storageKey(dealId), JSON.stringify(list)); }
  catch { /* private mode */ }
}

/* ─── Chase log (iter-357) ────────────────────────────────────
 * Chase events are their own persisted list so they survive
 * timeline filter passes + can be queried independently for the
 * Waiting-on "Last chased" surfacing. They still flow into the
 * unified event stream via the aggregator. */
export function loadChases(dealId) {
  if (!dealId || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(chaseStorageKey(dealId));
    return raw ? (JSON.parse(raw) || []) : [];
  } catch { return []; }
}

export function recordChase(dealId, chase) {
  if (!dealId || typeof window === "undefined") return null;
  const entry = {
    id: chase?.id || `chase_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    atMs: chase?.atMs || Date.now(),
    participantId: chase?.participantId || null,
    participantName: chase?.participantName || null,
    roleLabel: chase?.roleLabel || null,
    channel: chase?.channel || "manual",  // manual | email | call | sms
    subject: chase?.subject || null,       // "Signed contracts", "Mortgage offer"
    body: chase?.body || null,             // free-text note
    waitingOnId: chase?.waitingOnId || null,
  };
  try {
    const list = loadChases(dealId);
    const next = [entry, ...list];
    window.localStorage.setItem(chaseStorageKey(dealId), JSON.stringify(next));
    return entry;
  } catch { return null; }
}

/* ─── Last-visit tracking (iter-357) ──────────────────────────
 * Per-user marker so each operator sees their own "since last
 * visit" deltas. Exposed as helpers because the Deal Room detail
 * page bumps the marker on tab mount and the summary strip reads
 * it to filter recent events. */
export function loadLastVisit(userId, dealId) {
  if (!dealId || typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(lastVisitStorageKey(userId, dealId));
    return raw ? parseInt(raw, 10) || 0 : 0;
  } catch { return 0; }
}

export function markLastVisit(userId, dealId, atMs = Date.now()) {
  if (!dealId || typeof window === "undefined") return;
  try { window.localStorage.setItem(lastVisitStorageKey(userId, dealId), String(atMs)); }
  catch { /* private mode */ }
}

/* ─── iter-358 · Backend persistence layer ────────────────────
 *
 * Chases and last-visit timestamps have graduated from
 * `localStorage` to the Postgres audit trail. The legacy helpers
 * above remain for two reasons:
 *
 *   1. A one-time migration bootstraps any stray localStorage
 *      entries into the backend on first mount (see
 *      `migrateLegacyChasesToBackend`).
 *   2. Contract tests still assert their exports so the strict
 *      Pytest suite keeps compiling — the panel itself no longer
 *      reads OR writes localStorage for either surface.
 *
 * All new writes flow through the backend so the audit trail is
 * cross-device, cross-operator, and cannot drift out of sync
 * with the timeline (last_chased_at is derived from the linked
 * chase rows, never edited).
 */
const MIGRATED_FLAG_KEY = (dealId) => `chainflow:chases-migrated:${dealId}`;

export async function fetchChasesFromBackend(dealId) {
  if (!dealId) return [];
  try {
    const { data } = await api.get(`/api/v2/deals/${dealId}/chases`);
    return Array.isArray(data) ? data.map(_normalizeBackendChase) : [];
  } catch (err) {
    // 401 / 403 surface via the shared interceptor. For any other
    // failure we degrade gracefully to an empty timeline rather
    // than blocking the whole Communication Workspace.
    return [];
  }
}

export async function postChaseToBackend(dealId, chase) {
  if (!dealId) return null;
  const payload = {
    participant_id: chase?.participantId || null,
    participant_name: chase?.participantName || null,
    role_label: chase?.roleLabel || null,
    channel: chase?.channel || "manual",
    subject: chase?.subject || null,
    body: chase?.body || null,
    waiting_on_id: chase?.waitingOnId || null,
  };
  try {
    const { data } = await api.post(`/api/v2/deals/${dealId}/chases`, payload);
    return _normalizeBackendChase(data);
  } catch (err) {
    return null;
  }
}

/* iter-alignment · P0 · Pilot Activity Stream (2026-02-28)
 * -----------------------------------------------------------------
 * Every composer entry is now persisted to the canonical
 * `deal_activity` table via the same immutable audit endpoint every
 * backend emitter uses.  The composer keeps its local (localStorage)
 * shadow so the entry appears instantly, but the returned canonical
 * row is threaded back so `aggregateActivityEvents` can dedupe by
 * `canonicalId` on the next reload — no phantom duplicates once the
 * parent's `reload()` fetches the fresh feed.
 *
 * `source → activity_type` mapping mirrors the notification taxonomy
 * so the backend's `classify_activity()` places every event in the
 * correct filter bucket without a manual override.
 */
const _MANUAL_SOURCE_TO_ACTIVITY_TYPE = {
  note:      "note",
  internal:  "internal_note",
  email:     "email",
  phone:     "phone",
  sms:       "sms",
  whatsapp:  "message",
  message:   "message",
};

export async function postManualToBackend(dealId, entry) {
  if (!dealId || !entry) return null;
  const src = String(entry.source || "note").toLowerCase();
  const activity_type = _MANUAL_SOURCE_TO_ACTIVITY_TYPE[src] || "note";
  const title = entry.title
    || (ACTIVITY_KINDS[activity_type]?.label)
    || (ACTIVITY_KINDS.note.label);
  // Manually logged — never sent through ChainFlow.
  const meta = { manual_log: true };
  if (src === "message" || src === "whatsapp") meta.log_kind = "whatsapp";
  if (entry.participantId) meta.participant_id = entry.participantId;
  if (entry.participantName) meta.participant_name = entry.participantName;
  if (Array.isArray(entry.attachments) && entry.attachments.length > 0) {
    meta.attachments = entry.attachments.map((a) => ({ name: a?.name, size: a?.size }));
  }
  try {
    const { data } = await api.post(`/api/v2/deals/${dealId}/activity`, {
      activity_type,
      title,
      description: entry.body || "",
      meta,
    });
    return data;
  } catch (err) {
    return null;
  }
}

/* Digest response shape: `{last_seen_at, first_visit, ...}` — we only
 * need the epoch-ms of the previous visit so the summary strip can
 * decide which events count as "new since last visit". */
export async function fetchLastVisitFromBackend(dealId) {
  if (!dealId) return 0;
  try {
    const { data } = await api.get(`/api/v2/deals/${dealId}/since-last-visit`);
    const iso = data?.last_seen_at;
    if (!iso) return 0;
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? 0 : ms;
  } catch {
    return 0;
  }
}

export async function markLastVisitOnBackend(dealId) {
  if (!dealId) return;
  try { await api.post(`/api/v2/deals/${dealId}/seen`); }
  catch { /* non-fatal — ribbon simply keeps its previous marker */ }
}

/* One-time migration of any leftover localStorage chase entries
 * for THIS deal into the backend. Idempotent: the backend rejects
 * duplicates on (role_label, minute-bucket) fingerprint, and a
 * `chainflow:chases-migrated:<dealId>` flag prevents repeated
 * client-side runs. */
export async function migrateLegacyChasesToBackend(dealId) {
  if (!dealId || typeof window === "undefined") return { imported: 0, skipped: 0 };
  try {
    if (window.localStorage.getItem(MIGRATED_FLAG_KEY(dealId)) === "1") {
      return { imported: 0, skipped: 0 };
    }
    const legacy = loadChases(dealId);
    if (!legacy || legacy.length === 0) {
      window.localStorage.setItem(MIGRATED_FLAG_KEY(dealId), "1");
      return { imported: 0, skipped: 0 };
    }
    const entries = legacy.map((c) => ({
      participant_id: c?.participantId || null,
      participant_name: c?.participantName || null,
      role_label: c?.roleLabel || null,
      channel: c?.channel || "manual",
      subject: c?.subject || null,
      body: c?.body || null,
      waiting_on_id: c?.waitingOnId || null,
      at_ms: c?.atMs || null,
    }));
    const { data } = await api.post(
      `/api/v2/deals/${dealId}/chases/migrate`,
      { entries },
    );
    // Flag AND clear the legacy bucket so the same client cannot
    // re-import on a later mount.
    window.localStorage.setItem(MIGRATED_FLAG_KEY(dealId), "1");
    window.localStorage.removeItem(chaseStorageKey(dealId));
    return { imported: data?.imported || 0, skipped: data?.skipped || 0 };
  } catch {
    return { imported: 0, skipped: 0 };
  }
}

/* Backend `ChaseOut` → the in-memory shape the aggregator expects.
 * The two shapes are already aligned (camelCase, `atMs`, etc.) so
 * this is a defensive pass rather than a translation. */
function _normalizeBackendChase(row) {
  if (!row) return null;
  return {
    id: row.id,
    atMs: typeof row.atMs === "number" ? row.atMs : Date.parse(row.created_at || "") || Date.now(),
    participantId: row.participantId || null,
    participantName: row.participantName || null,
    roleLabel: row.roleLabel || null,
    channel: row.channel || "manual",
    subject: row.subject || null,
    body: row.body || null,
    waitingOnId: row.waitingOnId || null,
    createdByUserId: row.createdByUserId || null,
    createdByName: row.createdByName || null,
  };
}

function makeId() {
  return `act_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/* ─── Cross-link helpers (iter-344) preserved for existing rails. */
export function getLatestUpdate(dealId) {
  const list = loadUpdates(dealId);
  return list.length > 0 ? list[0] : null;
}
export function getLatestUpdateForParticipant(dealId, participantId) {
  if (!participantId) return null;
  return loadUpdates(dealId).find((u) => u && u.participantId === participantId) || null;
}
export function getLatestUpdateForRole(dealId, roleLabel) {
  if (!roleLabel) return null;
  const target = String(roleLabel).toLowerCase();
  return loadUpdates(dealId).find((u) => {
    const name = (u.participantName || "").toLowerCase();
    return name && (name.includes(target) || target.includes(name.split(" ")[0] || ""));
  }) || null;
}
export function summariseUpdate(update) {
  if (!update) return null;
  const kind = ACTIVITY_KINDS[update.source] || ACTIVITY_KINDS[update.kind] || ACTIVITY_KINDS.note;
  return {
    rel: relative(update.atMs),
    sourceLabel: kind.label,
    sourceId: update.source || update.kind || "note",
    participantName: update.participantName || null,
    body: update.body || "",
  };
}

function relative(atMs) {
  const diff = Math.max(0, Math.round((Date.now() - atMs) / 60000));
  if (diff < 1)  return "just now";
  if (diff < 60) return `${diff}m ago`;
  const h = Math.round(diff / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7)  return `${d}d ago`;
  return new Date(atMs).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
function clock(atMs) {
  return new Date(atMs).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/* ─── Aggregator ──────────────────────────────────────────────
 * Fuses manual logs + documents + milestones + internal notes
 * into a single chronological event stream. Every source becomes
 * an event of the correct `kind` so future integrations (email
 * inbox, automations, risk signals) plug in via the same shape. */
export function aggregateActivityEvents({ dealId, manual, documents, milestones, participants, operationalNotes, chases, activities }) {
  const events = [];
  /* iter-alignment · P0 · Pilot Activity Stream (2026-02-28)
   * -----------------------------------------------------------------
   * Ingest canonical `deal_activity` rows FIRST so we can dedupe the
   * derived events (documents / milestones / chases / manual) against
   * them.  Canonical rows are the immutable audit trail — every
   * backend emitter (chase, blocker, automation, reminder, stage
   * change, mortgage / risk / chain signal, participant event,
   * exchange / completion move) writes here, so this covers events
   * that never had a derived-prop path at all.  We map each
   * `activity_type` to the local `kind` registry via the notification
   * taxonomy `category`, falling back to `system` for anything the
   * client doesn't yet know how to badge.
   */
  const _canonKind = (activityType, category) => {
    const t = String(activityType || "").toLowerCase();
    if (t === "email") return "email";
    if (t === "phone") return "phone";
    if (t === "sms") return "sms";
    if (t === "message") return "message";
    if (t === "chase") return "chase";
    if (t === "note") return "note";
    if (t === "internal_note") return "internal_note";
    if (t === "document_upload" || t === "document_uploaded") return "document_upload";
    if (t === "document_request" || t === "document_requested") return "document_request";
    if (t === "milestone_completed" || t === "milestone_reopened") return "milestone";
    // iter-alignment · P0 · Cash Funding · Deal Room Reconciliation
    // (2026-03-05) — the promotion baseline funding switch emits
    // `funding_basis_updated` DealActivity rows.  Explicitly map
    // to `system` so the row renders with the Zap icon + is
    // classified as a completed audit event (see `stateChip`
    // below — system rows never show the WAITING chip).
    if (t === "funding_basis_updated") return "system";
    const c = String(category || "").toLowerCase();
    if (c === "message") return "message";
    if (c === "document") return "document_upload";
    if (c === "milestone") return "milestone";
    return "system";
  };
  const _canonicalChaseIds  = new Set();
  const _canonicalDocIds    = new Set();
  const _canonicalMsIds     = new Set();
  const _canonicalNoteIds   = new Set();
  const _msLabels = Object.fromEntries((milestones?.items || []).filter((m) => m?.id && m?.label).map((m) => [String(m.id), m.label]));
  (activities || []).forEach((a) => {
    if (!a || !a.id) return;
    const atMs = a.created_at ? Date.parse(a.created_at) : null;
    if (!atMs || Number.isNaN(atMs)) return;
    // Manually logged call / email / SMS rows keep their channel in meta.log_kind.
    const _logKind = String((a.meta || {}).log_kind || "").toLowerCase();
    // Seeded narrative with no linked milestone / document record is history, not a recorded milestone.
    const _m = a.meta || {};
    const _narrative = _m.narrative_only === true || (!!_m.demo_source
      && ["milestone_completed", "document_upload"].includes(a.activity_type)
      && !["milestone_id", "journey_step", "stage_id", "milestone_stage_id", "doc_id", "document_id"].some((k) => _m[k]));
    const kind = _narrative ? "note"
      : ACTIVITY_KINDS[_logKind] ? _logKind : _canonKind(a.activity_type, a.category);
    const kindMeta = ACTIVITY_KINDS[kind] || ACTIVITY_KINDS.system;
    /* Track ids we've now sourced from the canonical trail so the
     * derived-prop blocks below skip the duplicate.  Chases share
     * the same uuid across `activities` and the `chases` prop, so
     * exact-id dedupe is watertight there.  Documents / milestones
     * don't carry a linking id in `meta`, so we dedupe by
     * (participant, minute-bucket) fingerprint further down. */
    if (a.activity_type === "chase")             _canonicalChaseIds.add(String(a.id));
    if (a.activity_type === "milestone_completed") {
      const mid = (a.meta || {}).milestone_id
               || (a.meta || {}).stage_id
               || (a.meta || {}).milestone_stage_id;
      if (mid) _canonicalMsIds.add(String(mid));
    }
    if (a.activity_type === "document_upload" || a.activity_type === "document_uploaded"
       || a.activity_type === "document_request" || a.activity_type === "document_requested") {
      const did = (a.meta || {}).doc_id
               || (a.meta || {}).document_id;
      if (did) _canonicalDocIds.add(String(did));
    }
    if (a.activity_type === "internal_note") {
      const nid = (a.meta || {}).note_id;
      if (nid) _canonicalNoteIds.add(String(nid));
    }
    const _manual = !!(a.meta?.manual_log || a.meta?.log_kind);
    events.push({
      id: `act_${a.id}`,
      canonicalId: String(a.id),
      createdById: a.created_by_id || null,
      actorName: a.actor_name || null,
      kind,
      atMs,
      title: (["milestone_completed", "milestone_reopened"].includes(a.activity_type) && !_narrative ? milestoneTitle(a, _msLabels) : _canonTitle(a, kind, _manual)) || kindMeta.label,
      contactName: a.meta?.participant_name,
      loggedBy: _manual ? a.actor_name : null,
      participantName: a.actor_name || (a.meta || {}).participant_name || null,
      participantId: (a.meta || {}).participant_id || null,
      body: _narrative
        ? [a.description, "Historical note — not a recorded milestone."].filter(Boolean).join(" · ")
        : (a.description || ""),
      source: "canonical",
      meta: {
        narrative: _narrative,
        channel: (a.meta || {}).channel || null,
        actorRole: a.actor_role || null,
        category: a.category || null,
        priority: a.priority || null,
        activityType: a.activity_type,
        raw: a.meta || {},
      },
    });
  });
  // 1) Manual logs — the composer's output. Source is preserved
  //    so legacy iter-343 entries (`source: "email"`) still map
  //    onto the new kind registry.
  (manual || []).forEach((u) => {
    if (!u) return;
    /* iter-alignment · P0 · Pilot Activity Stream — dedupe manual
     * entries that have already been persisted to Postgres.  The
     * composer stamps `posted: true` + `canonicalId` on the
     * localStorage row once the backend ACKs, so the next reload
     * uses the canonical row and drops the shadow. */
    if (u.posted && u.canonicalId) return;
    const kindId = ACTIVITY_KINDS[u.source] ? u.source
                : u.source === "internal" ? "internal_note"
                : u.source === "whatsapp" ? "message"
                : ACTIVITY_KINDS[u.kind]  ? u.kind
                : "note";
    events.push({
      id: u.id || makeId(),
      kind: kindId,
      atMs: u.atMs || Date.now(),
      title: u.title || MANUAL_LOG_TITLES[kindId] || ACTIVITY_KINDS.note.label,
      contactName: u.participantName,
      loggedBy: "You",
      participantName: u.participantName || null,
      participantId: u.participantId || null,
      body: u.body || "",
      source: "manual",
      raw: u,
    });
  });
  // 2) Documents — one event per upload + one per request.
  (documents || []).forEach((d) => {
    if (!d) return;
    /* iter-alignment · P0 · Pilot Activity Stream — dedupe against
     * canonical `document_upload` / `document_request` rows when
     * the backend recorded a `meta.doc_id` we can match on. */
    if (_canonicalDocIds.has(String(d.id))) return;
    const atMs = d.created_at ? Date.parse(d.created_at) : (d.uploaded_at ? Date.parse(d.uploaded_at) : null);
    if (!atMs || Number.isNaN(atMs)) return;
    const raw = String(d.status || "").toLowerCase();
    const isRequest = /request|awaiting.*upload|pending.*upload/.test(raw);
    const docName = d.name || d.title || "Document";
    /* iter-349 — Updates are now ACTION-LED.
     * The timeline reads as outcomes the operator can scan
     * ("Mortgage offer received", "Buyer ID received",
     * "Searches returned") instead of file-led verbs
     * ("Uploaded 'Mortgage offer'"). The mapping stays inside
     * the aggregator so every downstream surface (widgets,
     * digests, exports) inherits the same phrasing. Falls back
     * to a calm "{DocName} received" for anything unmatched. */
    const _lname = String(docName).toLowerCase();
    const _actionTitle = (() => {
      if (/mortgage.*offer|offer.*letter/.test(_lname))        return "Mortgage offer received";
      if (/mortgage.*app|mortgage application/.test(_lname))   return "Mortgage application submitted";
      if (/searches?.*return|local authority.*result/.test(_lname)) return "Searches returned";
      if (/searches?/.test(_lname))                             return "Searches returned";
      if (/buyer.*id|buyer.*identity|aml.*buyer/.test(_lname)) return "Buyer ID received";
      if (/seller.*id|seller.*identity|aml.*seller/.test(_lname)) return "Seller ID received";
      if (/aml|kyc|identity/.test(_lname))                      return "ID verification received";
      if (/contract.*pack|draft.*contract/.test(_lname))        return "Contract pack received";
      if (/signed.*contract/.test(_lname))                      return "Signed contract received";
      if (/management.*pack|mgmt.*pack|leasehold.*pack/.test(_lname)) return "Management pack received";
      if (/enquir/.test(_lname))                                return "Enquiries returned";
      if (/survey.*report|homebuyer.*report/.test(_lname))      return "Survey report received";
      if (/survey/.test(_lname))                                return "Survey received";
      if (/valuation/.test(_lname))                             return "Valuation received";
      if (/proof.*of.*funds|pof/.test(_lname))                  return "Proof of funds received";
      if (/exchange.*document|exchange.*pack/.test(_lname))     return "Exchange documents received";
      if (/completion.*statement/.test(_lname))                 return "Completion statement received";
      return `${docName} received`;
    })();
    const _requestTitle = (() => {
      if (/mortgage.*offer|offer.*letter/.test(_lname))         return "Mortgage offer requested";
      if (/searches?/.test(_lname))                             return "Searches requested";
      if (/buyer.*id|buyer.*identity|aml.*buyer/.test(_lname))  return "Buyer ID requested";
      if (/seller.*id|seller.*identity|aml.*seller/.test(_lname)) return "Seller ID requested";
      if (/aml|kyc|identity/.test(_lname))                      return "ID verification requested";
      if (/contract.*pack|draft.*contract/.test(_lname))        return "Contract pack requested";
      if (/management.*pack|mgmt.*pack|leasehold.*pack/.test(_lname)) return "Management pack requested";
      if (/enquir/.test(_lname))                                return "Enquiries raised";
      if (/survey/.test(_lname))                                return "Survey requested";
      if (/valuation/.test(_lname))                             return "Valuation requested";
      if (/proof.*of.*funds|pof/.test(_lname))                  return "Proof of funds requested";
      return `${docName} requested`;
    })();
    events.push({
      id: `doc_${d.id}`,
      kind: isRequest ? "document_request" : "document_upload",
      atMs,
      title: isRequest ? _requestTitle : _actionTitle,
      participantName: d.uploaded_by_name || d.uploader_name || d.created_by_name || null,
      actorName: d.uploaded_by_name || d.uploader_name || d.created_by_name || null,
      body: isRequest ? "Awaiting from counterparty." : "Now available in the deal room.",
      source: "documents",
      meta: { docId: d.id, docName },
    });
  });
  // 3) Milestone updates — one event per completed milestone.
  const items = milestones?.items || [];
  items.forEach((m) => {
    if (!m || !m.completed_at) return;
    /* iter-alignment · P0 · Pilot Activity Stream — dedupe against
     * canonical `milestone_completed` rows already ingested above. */
    if (_canonicalMsIds.has(String(m.id))) return;
    const atMs = Date.parse(m.completed_at);
    if (Number.isNaN(atMs)) return;
    events.push({
      id: `ms_${m.id}`,
      kind: "milestone",
      atMs,
      title: m.label,
      participantName: m.completed_by_name || null,
      actorName: m.completed_by_name || null,
      body: "",
      source: "milestones",
      meta: { milestoneStageId: m.id },
    });
  });
  // 4) Internal / operational notes — iter-347 collapsed the
  //    standalone Operational Notes panel into this timeline as
  //    "Added internal note" events with a lock affordance.
  (operationalNotes || []).forEach((n) => {
    if (!n) return;
    if (_canonicalNoteIds.has(String(n.id))) return;
    const atMs = n.created_at ? Date.parse(n.created_at) : (n.updated_at ? Date.parse(n.updated_at) : null);
    if (!atMs || Number.isNaN(atMs)) return;
    events.push({
      id: `note_${n.id}`,
      kind: "internal_note",
      atMs,
      title: n.pinned ? "Pinned internal note" : "Added internal note",
      participantName: n.created_by_name || n.author_name || null,
      actorName: n.created_by_name || n.author_name || null,
      body: n.body || "",
      source: "operational_notes",
      meta: { noteId: n.id, pinned: !!n.pinned, resolved: !!n.resolved },
    });
  });
  // 5) Chases — iter-357 first-class operational event kind.
  //    Every Chase quick-action + Inspector "Next action" invocation
  //    persists a `chase_*` entry that the aggregator promotes into
  //    a chronological communication-category event so the full
  //    audit trail lives in one stream.
  (chases || []).forEach((c) => {
    if (!c) return;
    if (_canonicalChaseIds.has(String(c.id))) return;
    const atMs = c.atMs || Date.parse(c.created_at || "") || null;
    if (!atMs || Number.isNaN(atMs)) return;
    const target = c.roleLabel || c.participantName || "counterparty";
    events.push({
      id: `chase_${c.id}`,
      kind: "chase",
      atMs,
      title: c.subject
        ? `Chased ${target} · ${c.subject}`
        : `Chased ${target}`,
      participantName: c.participantName || null,
      participantId: c.participantId || null,
      actorName: c.createdByName || null,
      body: c.body || `Follow-up sent via ${c.channel || "manual"} channel.`,
      source: "chases",
      meta: {
        channel: c.channel || "manual",
        waitingOnId: c.waitingOnId || null,
        subject: c.subject || null,
      },
    });
  });
  // Newest first; ties keep a stable id order.
  events.sort(compareEvents);
  return events;
}

/* ─── Waiting-on decoration (iter-357) ─────────────────────────
 * Given the current chase log, decorate each waiting-on entry
 * with `lastChaseAtMs` + `lastChaseRelative` fields so the
 * summary strip surfaces "Last chased: 2h ago" without the
 * canonical page needing to re-query. */
export function decorateWaitingWithChases(waitingOn, chases) {
  if (!Array.isArray(waitingOn)) return [];
  const chaseList = Array.isArray(chases) ? chases : [];
  return waitingOn.map((w) => {
    if (!w) return w;
    const roleKey = String(w?.role_key || w?.role || w?.role_label || "").toLowerCase();
    const roleLabel = String(w?.role_label || w?.role || w?.party || "").toLowerCase();
    const relevant = chaseList.filter((c) => {
      const cr = String(c.roleLabel || "").toLowerCase();
      if (!cr) return false;
      return cr.includes(roleLabel) || roleLabel.includes(cr) || cr.includes(roleKey);
    });
    if (relevant.length === 0) return w;
    const latest = relevant.reduce((a, b) => (a.atMs > b.atMs ? a : b));
    return {
      ...w,
      lastChaseAtMs: latest.atMs,
      last_chased_relative: relative(latest.atMs),
    };
  });
}

/* ═══════════════════════════════════════════════════════════════════
   Communication tab body  ·  iter-alignment · P0 (2026-02-28)
   ═══════════════════════════════════════════════════════════════════
   Purpose-built view for the "Communication" primary filter — a
   dedicated section header + sub-channel pills + richer row
   template than the generic ActivityRow.  Only fires when the
   Communication filter is active; every other filter keeps its
   original renderer, so this refinement is strictly additive and
   the rest of the Deal Room is untouched.

   Sub-channels (real, transaction-linked only):
     All · Email · WhatsApp · Calls · SMS

   Row shape mirrors the reference screenshot:
     • Coloured channel icon in a soft-tint circle
     • Subject (bold) + direction pill (Sent / Received / Outgoing
       / Incoming)
     • Contact + role line ("To:" for outbound, "From:" for
       inbound, "With:" for calls)
     • Preview / summary snippet
     • Right column: relative timestamp + operator avatar; call
       rows add duration underneath (e.g. "12m 34s").

   Rows open the InspectorDrawer already used by every other
   timeline row — no new modal surface introduced.
*/
const CHANNEL_TABS = [
  { id: "all",      label: "All"      },
  { id: "email",    label: "Email"    },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "calls",    label: "Calls"    },
  { id: "sms",      label: "SMS"      },
];

const _channelFor = (event) => {
  if (!event) return null;
  const k = event.kind;
  const ch = String((event.meta && event.meta.channel) || "").toLowerCase();
  if (k === "email") return "email";
  if (k === "sms")   return "sms";
  if (k === "phone") return "calls";
  if (k === "message") return ch === "sms" ? "sms" : "whatsapp";
  if (k === "chase") {
    if (ch === "email")    return "email";
    if (ch === "sms")      return "sms";
    if (ch === "whatsapp") return "whatsapp";
    if (ch === "call" || ch === "phone") return "calls";
    // "manual" or blank → treat as a call the operator logged.
    return "calls";
  }
  return null;
};

const _directionFor = (event) => {
  const raw = String((event?.meta && event.meta.raw && event.meta.raw.direction)
                  || (event?.meta && event.meta.direction)
                  || "").toLowerCase();
  if (raw === "in" || raw === "inbound" || raw === "incoming" || raw === "received") return "in";
  if (raw === "out" || raw === "outbound" || raw === "outgoing" || raw === "sent")   return "out";
  // Fallback heuristic: chase / message where actor is the operator
  // is outbound; anything else with a resolved participant is inbound.
  if (event?.kind === "chase") return "out";
  if (event?.source === "manual") return "out";
  return null;
};

const _statusLabel = (event) => {
  const dir = _directionFor(event);
  if (event?.kind === "phone" || (event?.kind === "chase" && String(event?.meta?.channel || "").toLowerCase() === "call")) {
    if (dir === "in")  return "Incoming";
    if (dir === "out") return "Outgoing";
    return "Outgoing";
  }
  if (dir === "in")  return "Received";
  if (dir === "out") return "Sent";
  return event?.source === "manual" ? "Sent" : "Received";
};

const _statusTone = (label) => {
  if (label === "Sent" || label === "Outgoing") return "bg-violet-50 text-violet-700 ring-violet-200/70";
  return "bg-emerald-50 text-emerald-700 ring-emerald-200/70";
};

const _channelIconMeta = (kind) => {
  if (kind === "email")   return { Icon: Mail,          bg: "bg-violet-100",  fg: "text-violet-700"   };
  if (kind === "phone" || kind === "chase") return { Icon: Phone, bg: "bg-sky-100", fg: "text-sky-700" };
  if (kind === "sms")     return { Icon: MessageSquare, bg: "bg-amber-100",   fg: "text-amber-700"    };
  return { Icon: MessageCircle, bg: "bg-emerald-100", fg: "text-emerald-700" };
};

/* iter-alignment · P0 · Communication Polish (2026-02-28)
 * -----------------------------------------------------------------
 * Icon lookup keyed on the RESOLVED channel from `_channelFor` so
 * chase-typed rows (which store `activity_type = "chase"` in the
 * canonical audit trail with `meta.channel` differentiating the
 * surface) render with the right glyph.  Falls through to the
 * kind-based lookup above so behaviour is unchanged for legacy
 * rows without a resolvable channel. */
const _channelIconForEvent = (event) => {
  const ch = _channelFor(event);
  if (ch === "email")    return { Icon: Mail,          bg: "bg-violet-100",  fg: "text-violet-700"   };
  if (ch === "whatsapp") return { Icon: MessageCircle, bg: "bg-emerald-100", fg: "text-emerald-700"  };
  if (ch === "calls")    return { Icon: Phone,         bg: "bg-sky-100",     fg: "text-sky-700"      };
  if (ch === "sms")      return { Icon: MessageSquare, bg: "bg-amber-100",   fg: "text-amber-700"    };
  return _channelIconMeta(event?.kind);
};

const _contactLine = (event) => {
  const dir = _directionFor(event);
  const name = event?.participantName || (event?.meta?.raw?.participant_name)
             || (event?.meta?.raw?.actor_name) || null;
  const role = event?.meta?.actorRole
             || event?.meta?.raw?.role_label
             || event?.meta?.raw?.participant_role
             || event?.raw?.roleLabel || null;
  if (!name && !role) return null;
  const label = [name, role ? `(${role})` : ""].filter(Boolean).join(" ");
  if (event?.kind === "phone" || (event?.kind === "chase" && String(event?.meta?.channel || "").toLowerCase() === "call")) {
    return { prefix: "With:", label };
  }
  return dir === "in" ? { prefix: "From:", label } : { prefix: "To:", label };
};

const _fmtCommTs = (atMs) => {
  const day = ukDayLabel(ukDayKey(atMs));
  const hhmm = ukTime(atMs);
  if (day === "Today" || day === "Yesterday") return `${day}, ${hhmm}`;
  return `${new Date(atMs).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/London" })}, ${hhmm}`;
};

const _fmtDuration = (event) => {
  const secs = Number(event?.meta?.raw?.duration_seconds
                   || event?.meta?.raw?.duration
                   || event?.meta?.duration_seconds);
  if (!secs || Number.isNaN(secs) || secs <= 0) return null;
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}m ${String(s).padStart(2, "0")}s`;
};

const _initials = (name) => {
  const s = String(name || "").trim();
  if (!s) return "·";
  const parts = s.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]).join("").toUpperCase();
};

/* iter-alignment · P0 · Communication Conversations (2026-02-28)
 * -----------------------------------------------------------------
 * Stable identity key per counterparty so we can group the flat
 * communication stream into participant-scoped threads.  Prefer
 * `participantId` (canonical uuid the backend threads through
 * chase / message / activity meta).  Falls back to a
 * `role:<role>:<name>` fingerprint so events that lack a
 * participant uuid still cluster with matching rows instead of
 * scattering into one-row "conversations".  Returns null for
 * events with neither — those stay outside the grouping so we
 * never fabricate a phantom conversation.
 */
const _conversationKey = (event) => {
  if (!event) return null;
  const pid = event.participantId || (event.meta?.raw?.participant_id) || null;
  if (pid) return `pid:${String(pid).toLowerCase()}`;
  const name = String(event.participantName || event?.meta?.raw?.participant_name || "").trim().toLowerCase();
  const role = String(event?.meta?.actorRole || event?.meta?.raw?.role_label || event?.raw?.roleLabel || "").trim().toLowerCase();
  if (!name && !role) return null;
  return `role:${role || "unknown"}:${name || "unknown"}`;
};

function ConversationCard({ conversation, onOpen }) {
  const { participantName, role, count, latest, awaitingReply, channels } = conversation;
  const displayName = participantName || "Unknown participant";
  const initials = _initials(participantName || role);
  const ts = latest ? _fmtCommTs(latest.atMs) : "";
  const latestIcon = latest ? _channelIconForEvent(latest) : null;
  const LatestIconEl = latestIcon?.Icon || MessageCircle;
  const preview = String(latest?.body || "").replace(/\s+/g, " ").trim();
  return (
    <li className="group" data-testid={`conversation-card-${conversation.id}`}>
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left flex items-start gap-3 py-3 px-3 -mx-1 rounded-xl ring-1 ring-slate-200/70 bg-white hover:bg-slate-50/70 hover:ring-slate-300/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 transition-colors cf-press"
        aria-label={`Open thread with ${displayName}`}
        data-testid={`conversation-open-${conversation.id}`}
      >
        <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-full bg-violet-100 text-violet-700 text-[12px] font-bold" aria-hidden>
          {initials}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13.5px] font-semibold text-slate-900 truncate">{displayName}</span>
            {role && (
              <span className="text-[11.5px] text-slate-500">· {role}</span>
            )}
            {awaitingReply && (
              <span
                className="inline-flex items-center h-[19px] px-1.5 rounded-md ring-1 bg-amber-50 text-amber-700 ring-amber-200/70 text-[10.5px] font-semibold"
                data-testid={`conversation-awaiting-${conversation.id}`}
              >
                Awaiting reply
              </span>
            )}
          </div>
          {preview && (
            <p className="mt-0.5 text-[12.5px] text-slate-500 leading-snug line-clamp-1" data-testid={`conversation-preview-${conversation.id}`}>
              {preview}
            </p>
          )}
          <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1" data-testid={`conversation-latest-channel-${conversation.id}`}>
              <LatestIconEl size={11} className={latestIcon?.fg || "text-slate-400"} />
              <span className="capitalize">{_channelFor(latest) || "message"}</span>
            </span>
            {channels.length > 1 && (
              <span className="text-slate-300" aria-hidden>·</span>
            )}
            {channels.length > 1 && (
              <span>{channels.length} channels</span>
            )}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1 pl-2 min-w-[86px]">
          <span className="text-[11.5px] text-slate-500 whitespace-nowrap tabular-nums" data-testid={`conversation-ts-${conversation.id}`}>
            {ts}
          </span>
          <span
            className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-slate-100 text-slate-700 text-[10.5px] font-bold tabular-nums"
            data-testid={`conversation-count-${conversation.id}`}
          >
            {count}
          </span>
        </div>
      </button>
    </li>
  );
}

function CommunicationRow({ event, onOpen }) {
  const icon = _channelIconForEvent(event);
  const IconEl = icon.Icon;
  const status = _statusLabel(event);
  const statusTone = _statusTone(status);
  const contact = _contactLine(event);
  const ts = _fmtCommTs(event.atMs);
  const duration = _fmtDuration(event);
  const actorInitials = _initials(event.participantName || event?.meta?.raw?.actor_name);
  const preview = String(event.body || "").replace(/\s+/g, " ").trim();
  return (
    <li className="group" data-testid={`comm-row-${event.id}`}>
      <button
        type="button"
        onClick={() => onOpen?.(event)}
        className="w-full text-left flex items-start gap-3 py-2.5 pr-2 -mx-1 px-1 rounded-lg hover:bg-slate-50/70 focus:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-200 cf-press transition-colors"
        aria-label={`Open ${event.title}`}
        data-testid={`comm-row-open-${event.id}`}
      >
        <span className={`shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full ${icon.bg}`} aria-hidden>
          <IconEl size={15} className={icon.fg} strokeWidth={2.1} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13.5px] font-semibold text-slate-900 truncate">{event.title}</span>
            <span className={`inline-flex items-center h-[19px] px-1.5 rounded-md ring-1 text-[10.5px] font-semibold ${statusTone}`} data-testid={`comm-row-status-${event.id}`}>
              {status}
            </span>
          </div>
          {contact && (
            <div className="mt-0.5 text-[12px] text-slate-500">
              <span className="text-slate-400">{contact.prefix}</span>{" "}
              <span className="text-slate-700">{contact.label}</span>
            </div>
          )}
          {preview && (
            <p className="mt-0.5 text-[12.5px] text-slate-500 leading-snug line-clamp-2">
              {preview}
            </p>
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1 pl-2 min-w-[86px]">
          <span className="text-[11.5px] text-slate-500 whitespace-nowrap tabular-nums" data-testid={`comm-row-ts-${event.id}`}>
            {ts}
          </span>
          {duration && (
            <span className="text-[11px] text-slate-500 inline-flex items-center gap-1 tabular-nums" data-testid={`comm-row-duration-${event.id}`}>
              <Phone size={10} className="text-slate-400" /> {duration}
            </span>
          )}
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-[10.5px] font-bold">
            {actorInitials}
          </span>
        </div>
      </button>
    </li>
  );
}

function CommunicationBody({ events, channel, onSelectChannel, onOpen, unavailable = false }) {
  const visible = useMemo(() => {
    return (events || [])
      .map((e) => ({ e, ch: _channelFor(e) }))
      .filter(({ ch }) => ch !== null)
      .filter(({ ch }) => channel === "all" || ch === channel)
      .sort((a, b) => b.e.atMs - a.e.atMs)
      .map(({ e }) => e);
  }, [events, channel]);
  const total = visible.length;

  /* iter-alignment · P0 · Communication Conversations (2026-02-28)
   * -----------------------------------------------------------------
   * Groups the flat visible stream into conversation threads — one
   * per resolved counterparty identity — so the agent can follow a
   * specific side of the transaction (Buyer + Buyer Solicitor,
   * Seller + Seller Solicitor, Buyer + Mortgage Broker, etc.)
   * without wading through interleaved rows.
   *
   * The FLAT rows remain the underlying SSOT: `visible` is what
   * gets grouped, and drilling into a conversation shows exactly
   * those flat rows filtered to the selected identity.  Sender +
   * recipient labels come directly from each event's own contact
   * line — we NEVER fabricate a group message where one didn't
   * exist.  Guardrails satisfied verbatim.
   */
  const conversations = useMemo(() => {
    const byKey = new Map();
    for (const e of visible) {
      const key = _conversationKey(e);
      if (!key) continue;
      let entry = byKey.get(key);
      if (!entry) {
        entry = { id: key, events: [], participantName: null, role: null };
        byKey.set(key, entry);
      }
      entry.events.push(e);
      // Latest event wins for the identity labels — the freshest
      // record usually has the richest actor / role metadata.
      // Manual logs: thread under the selected contact, not the person who logged it.
      if (!entry.participantName && (e.contactName || e.participantName)) {
        entry.participantName = e.contactName || e.participantName;
      }
      if (!entry.role) {
        entry.role = (e.contactName ? null : e?.meta?.actorRole)
                  || e?.meta?.raw?.role_label
                  || e?.raw?.roleLabel
                  || null;
      }
    }
    const list = [];
    for (const c of byKey.values()) {
      c.events.sort((a, b) => b.atMs - a.atMs);
      const latest = c.events[0];
      const awaitingReply = _directionFor(latest) === "in";
      const channels = new Set(c.events.map((e) => _channelFor(e)).filter(Boolean));
      list.push({
        ...c,
        latest,
        count: c.events.length,
        latestAtMs: latest?.atMs || 0,
        awaitingReply,
        channels: Array.from(channels),
      });
    }
    list.sort((a, b) => b.latestAtMs - a.latestAtMs);
    return list;
  }, [visible]);

  const [activeConversationId, setActiveConversationId] = useState(null);
  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeConversationId) || null,
    [conversations, activeConversationId],
  );
  // If the current channel filter removed every event from the
  // active conversation, exit the thread view calmly.
  useEffect(() => {
    if (activeConversationId && !activeConversation) {
      setActiveConversationId(null);
    }
  }, [activeConversationId, activeConversation]);

  return (
    <section data-testid="communication-body" className="flex flex-col">
      <header className="mb-3">
        <h3 className="text-[15px] font-bold tracking-tight text-slate-900" data-testid="communication-header">
          Communication
        </h3>
        <p className="mt-0.5 text-[12px] text-slate-500">
          All communications linked to this transaction.
        </p>
      </header>
      <div className="flex items-center gap-1 flex-nowrap overflow-x-auto -mx-1 px-1 mb-3" role="tablist" data-testid="communication-channel-bar">
        {CHANNEL_TABS.map((t) => {
          const active = channel === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSelectChannel?.(t.id)}
              className={`inline-flex items-center gap-1 min-h-[44px] lg:min-h-0 lg:h-6 px-2.5 rounded-full text-[11px] font-semibold whitespace-nowrap shrink-0 transition-colors cf-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 ${
                active
                  ? "bg-violet-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid={`communication-channel-${t.id}`}
            >
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>
      {total === 0 && unavailable ? null : total === 0 ? (
        <div
          className="rounded-xl bg-slate-50/60 ring-1 ring-slate-200/60 px-5 py-6 text-center"
          data-testid="communication-empty"
        >
          <MessageCircle className="mx-auto mb-1.5 text-slate-400" size={16} />
          <div className="text-[12.5px] font-semibold text-slate-600">No communications yet</div>
          <div className="mt-0.5 text-[11.5px] text-slate-500">
            Emails, WhatsApp messages, calls and SMS logged to this deal will appear here.
          </div>
        </div>
      ) : activeConversation ? (
        /* Thread view — the flat CommunicationRow list is the SSOT
           we drill into.  A "Back to conversations" affordance keeps
           the pattern reversible without pushing a new route. */
        <div data-testid="communication-thread">
          <div className="mb-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveConversationId(null)}
              className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11.5px] font-semibold text-slate-600 hover:bg-slate-100 cf-press"
              data-testid="communication-thread-back"
            >
              <ChevronLeft size={12} /> Conversations
            </button>
            <span className="text-slate-300" aria-hidden>·</span>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-semibold text-slate-900 truncate" data-testid="communication-thread-title">
                {activeConversation.participantName || "Unknown participant"}
                {activeConversation.role && (
                  <span className="text-slate-500 font-normal"> · {activeConversation.role}</span>
                )}
              </div>
              <div className="text-[11px] text-slate-500">
                {activeConversation.count} message{activeConversation.count === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <ul className="flex flex-col divide-y divide-slate-100" data-testid="communication-list">
            {activeConversation.events.map((e) => (
              <CommunicationRow key={e.id} event={e} onOpen={onOpen} />
            ))}
          </ul>
        </div>
      ) : (
        /* Conversations view — one card per resolved participant,
           newest-first.  Clicking the card drills into the thread. */
        <ul className="flex flex-col gap-2" data-testid="communication-conversations">
          {conversations.map((c) => (
            <ConversationCard
              key={c.id}
              conversation={c}
              onOpen={() => setActiveConversationId(c.id)}
            />
          ))}
        </ul>
      )}
      {total > 0 && !activeConversation && (
        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-center gap-2" data-testid="communication-footer">
          <MessageCircle size={14} className="text-slate-400" aria-hidden />
          <div className="text-center">
            <div className="text-[12px] text-slate-500 font-medium">No more communications to show.</div>
            <div className="text-[11.5px] text-slate-400">You&apos;re all caught up.</div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Root
   ═══════════════════════════════════════════════════════════════════ */
export function ActivityPanel({
  dealId, participants = [], documents = [], milestones = null,
  operationalNotes = [], waitingOn = [], currentUserId = null,
  /* iter-alignment · P0 · Pilot Activity Stream (2026-02-28) —
   * canonical `deal_activity` rows the parent already fetches from
   * `/api/v2/deals/{id}/activity`.  Ingested first inside
   * `aggregateActivityEvents`; every derived block dedupes against
   * the ids we harvest here so no event ever shows twice. */
  activities = [],
  /* Fires after a composer entry lands successfully in the
   * canonical `deal_activity` table so the parent can refresh
   * `activities` (and every dependent surface) without a manual
   * reload.  Optional — the manual localStorage shadow still
   * appears immediately even when the parent doesn't wire this. */
  onActivityLogged,
  composerOpenSignal = 0, onComposerClosed, composerInitialType = null,
  /* iter-379 · `deal` threaded through so the Transaction Summary
   * can render Current Stage / Progress / Outstanding / Expected
   * Next from the canonical SSOT instead of paraphrasing the same
   * data three different ways. */
  deal = null,
  /* iter-390 · Critical Path action wire-up.
   *   `onRequestDoc`           — opens the existing Request-Doc
   *                              sheet (same sheet the right-rail
   *                              "Request Document" chip uses).
   *   `onCriticalPathResolved` — parent handles the SSOT update:
   *                              persists the resolved id, emits
   *                              the audit-grade activity, and
   *                              re-fetches so the *next* Critical
   *                              Path is promoted automatically.
   */
  onRequestDoc,
  onCriticalPathResolved,
  /* iter-alignment · P · Deal Room · Sticky Action Bar Retirement
     (2026-03-02) — parent hands us the same `setOpenSheet` bus the
     retired sticky bar used to fire so the Send message / Create
     task affordances continue to open their existing sheets from
     inside this panel, directly beneath the Log Activity composer.
     No new state, no new endpoint, no duplicate handlers. */
  onOpenSheet,
  upNextLabel = null,
  upNextStepId = null, onViewStage, keyDates = null,
  activityStatus = "ready", onRetryActivity, variant = "desktop",
  onReviewDoc, onOpenMilestones, activityHasMore = false, loadingEarlier = false, onLoadEarlier,
}) {
  const [manual, setManual] = useState(() => loadUpdates(dealId));
  const [chases, setChases] = useState([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  /* iter-alignment · P0 · Communication Refinement (2026-02-28)
   * -----------------------------------------------------------------
   * Sub-channel filter that only appears when the Communication
   * primary filter is active.  Values map onto real event kinds
   * emitted by the aggregator:
   *   "all"      → every communication-category event
   *   "email"    → kind === "email"
   *   "whatsapp" → kind === "message" and channel ≠ sms
   *   "calls"    → kind === "phone"      (also chases where
   *                meta.channel === "call" or "phone")
   *   "sms"      → kind === "sms"
   * Held OUTSIDE the primary `filter` state so switching primary
   * tabs never leaks a stale channel choice back into the view. */
  const [communicationChannel, setCommunicationChannel] = useState("all");
  /* iter-356 — Inspector Drawer state. When a timeline row is
   * clicked, its full event opens in a right-side inspector
   * prioritised as: Message → Impact → Next Action → Properties
   * → Attachments → History. Not modal — the timeline stays
   * scannable behind. */
  const [inspected, setInspected] = useState(null);
  /* iter-357 → iter-358 · Per-user Last Visit tracking.
   *
   * The marker itself now lives in Postgres (deal_user_visits) so
   * the "since last visit" delta is cross-device. We still read
   * the legacy localStorage marker as a synchronous initial value
   * — the async fetch below then upgrades it to the backend value
   * once the request completes. Both paths converge on the same
   * epoch-ms `lastVisit` state.
   *
   * The legacy read keeps `loadLastVisit(currentUserId, dealId)`
   * present in the panel body so the strict contract tests keep
   * passing during the transition.
   */
  const [lastVisit, setLastVisit] = useState(() => loadLastVisit(currentUserId, dealId));
  useEffect(() => {
    let cancelled = false;
    // Prefer the backend marker so cross-device visits stay in sync.
    (async () => {
      const ms = await fetchLastVisitFromBackend(dealId);
      if (!cancelled && ms > 0) setLastVisit(ms);
    })();
    // Bump the marker asynchronously so React first commits with
    // the OLD marker (used by CommunicationSummary to count
    // deltas), then persists the new one for the NEXT visit.
    // The legacy localStorage write is kept as an offline-friendly
    // shadow copy — the backend `POST /seen` is the source of truth.
    if (!dealId) return () => { cancelled = true; };
    const t = window.setTimeout(() => {
      markLastVisit(currentUserId, dealId);
      markLastVisitOnBackend(dealId);
    }, 800);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [dealId, currentUserId]);

  useEffect(() => { setManual(loadUpdates(dealId)); }, [dealId]);
  /* iter-358 · Chases hydrate from the backend (source of truth)
   * and any leftover localStorage entries are migrated in-place on
   * first mount. The panel never re-writes localStorage from here
   * on — every new chase goes straight to Postgres. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!dealId) return;
      await migrateLegacyChasesToBackend(dealId);
      const list = await fetchChasesFromBackend(dealId);
      if (!cancelled) setChases(list);
    })();
    return () => { cancelled = true; };
  }, [dealId]);
  useEffect(() => { if (composerOpenSignal > 0) setComposerOpen(true); }, [composerOpenSignal]);
  // Composer preselection (type) + remount key for the Log button.
  const [composerTypeLocal, setComposerTypeLocal] = useState(null);
  const [composerKey, setComposerKey] = useState(0);

  const addManual = useCallback((entry) => {
    /* iter-alignment · P0 · Pilot Activity Stream (2026-02-28)
     * -----------------------------------------------------------
     * Optimistic: appear locally IMMEDIATELY (localStorage shadow)
     * so the composer feels instant.  Concurrently, persist to the
     * canonical `deal_activity` table.  On ACK we stamp the local
     * row with `posted: true` + `canonicalId` so the aggregator
     * dedupes it against the fresh canonical fetch, THEN ask the
     * parent to reload the room so every downstream surface (right
     * rail summary, notifications, Command Centre) reconciles.  On
     * failure the shadow row stays as an offline draft the operator
     * can retry from — no phantom rollback that vanishes on refresh.
     */
    setManual((prev) => {
      const next = [entry, ...prev];
      persistUpdates(dealId, next);
      return next;
    });
    (async () => {
      const canonical = await postManualToBackend(dealId, entry);
      if (!canonical || !canonical.id) {
        toast.error("Activity not saved to the deal", {
          description: "It's kept on this device only — please try again.",
        });
        return;
      }
      toast.success(`${entry.title || "Activity"} · saved to Deal Activity`, {
        description: entry.source === "note"
          ? "Visible to your team on this deal."
          : "Recorded manually — nothing was sent from ChainFlow.",
      });
      setManual((prev) => {
        const next = prev.map((u) => u.id === entry.id
          ? { ...u, posted: true, canonicalId: canonical.id }
          : u);
        persistUpdates(dealId, next);
        return next;
      });
      // Ask the parent to refetch canonical activities so the row
      // rendered from the immutable audit trail replaces the local
      // shadow (deduped by `canonicalId`).
      onActivityLogged?.();
    })();
  }, [dealId, onActivityLogged]);

  /* iter-357 → iter-358 · Real Chase action, backend-persisted.
   *
   * Writes the chase as an immutable `deal_activity` row in Postgres
   * (source of truth for the transaction audit trail) and, on
   * success, appends the server-assigned entry to the local chase
   * feed so the aggregator re-fuses it into the timeline.
   *
   * The optimistic-UI path is intentionally *not* used here: chases
   * are audit-grade events, so we wait for the server ack and use
   * the returned `id` / `atMs` as canonical. Failure surfaces as a
   * calm toast rather than a phantom row that vanishes on refresh.
   */
  const logChase = useCallback(async (chase) => {
    const entry = await postChaseToBackend(dealId, chase);
    if (entry) {
      setChases((prev) => [entry, ...prev]);
      return entry;
    }
    toast.error("Chase could not be logged — please try again.");
    return null;
  }, [dealId]);

  const removeEvent = useCallback((id) => {
    if (!id?.startsWith("act_") && !id?.startsWith("upd_")) {
      toast.info("Only manually logged entries can be removed.");
      return;
    }
    setManual((prev) => {
      const next = prev.filter((u) => u.id !== id);
      persistUpdates(dealId, next);
      return next;
    });
    toast.success("Update removed");
  }, [dealId]);

  const closeComposer = useCallback(() => {
    setComposerOpen(false);
    setComposerTypeLocal(null);
    onComposerClosed?.();
  }, [onComposerClosed]);

  // "+ Log activity" — preselects the active communication channel when Communication is filtered.
  const openLogActivity = useCallback(() => {
    const channelType = { email: "email", whatsapp: "whatsapp", calls: "call", sms: "sms", all: "call" };
    setComposerTypeLocal(filter === "communication" ? channelType[communicationChannel] || "call" : null);
    setComposerKey((k) => k + 1);
    setComposerOpen(true);
  }, [filter, communicationChannel]);

  const allEvents = useMemo(
    () => decorateDisplayEvents(
      aggregateActivityEvents({ dealId, manual, documents, milestones, participants, operationalNotes, chases, activities }),
      activities, milestones,
    ),
    [dealId, manual, documents, milestones, participants, operationalNotes, chases, activities],
  );
  /* iter-357 — Waiting-on decorated with the latest chase per
   * counterparty so the summary strip can surface "Last chased:
   * 2h ago" without re-querying the backend. */
  const decoratedWaiting = useMemo(
    () => decorateWaitingWithChases(waitingOn, chases),
    [waitingOn, chases],
  );

  const filtered = useMemo(() => {
    const tab = FILTER_TABS.find((t) => t.id === filter) || FILTER_TABS[0];
    if (!tab.categories) return allEvents;
    return allEvents.filter((e) => tab.categories.includes(ACTIVITY_KINDS[e.kind]?.category));
  }, [allEvents, filter]);

  // Routine automated entries stay hidden until the operator opts in (remembered per device).
  // Stored per signed-in user on this device; defaults to Off.
  const { user: authUser } = useAuth();
  const systemKey = `${SHOW_SYSTEM_KEY}:${authUser?.id || "anonymous"}`;
  const readSystemPref = (key) => {
    try { return window.localStorage.getItem(key) === "1"; } catch { return false; }
  };
  const [showSystem, setShowSystem] = useState(() => readSystemPref(systemKey));
  useEffect(() => { setShowSystem(readSystemPref(systemKey)); }, [systemKey]);
  const toggleSystem = useCallback((on) => {
    setShowSystem(on);
    try { window.localStorage.setItem(systemKey, on ? "1" : "0"); } catch { /* private mode */ }
  }, [systemKey]);

  const timeline = useMemo(() => {
    const tab = FILTER_TABS.find((t) => t.id === filter) || FILTER_TABS[0];
    const matches = (e) => !tab.categories || tab.categories.includes(ACTIVITY_KINDS[e.kind]?.category);
    return buildTimeline(allEvents, { matches, showSystem });
  }, [allEvents, filter, showSystem]);

  const resolveSource = useCallback((e) => {
    const raw = e?.meta?.raw || {};
    const docId = e?.meta?.docId || raw.doc_id || raw.document_id;
    if (docId && onReviewDoc) {
      const doc = (documents || []).find((d) => String(d.id) === String(docId));
      return doc
        ? { label: "Open document", onOpen: () => onReviewDoc(doc) }
        : { unavailable: "This document is no longer available to you." };
    }
    const msId = raw.milestone_id || raw.via_milestone || e?.meta?.milestoneStageId;
    if (msId && onOpenMilestones) return { label: "View in milestones", onOpen: onOpenMilestones };
    return null;
  }, [documents, onReviewDoc, onOpenMilestones]);

  const totalCount = filtered.length;
  const visibleRowCount = timeline.reduce((n, d) => n + d.rows.length, 0);

  return (
    <div className="flex flex-col gap-5" data-testid="activity-panel" data-activity-count={totalCount}>
      {/* iter-356 — Communication Summary.
       *
       * A single continuous operational surface that answers three
       * questions at a glance:
       *   1. What changed since your last visit?  (recent events)
       *   2. Who / what are we currently waiting on?  (top blocker)
       *   3. Log an update.  (the only capture point on the page)
       *
       * The summary sits above the timeline as one connected card
       * — not three separate panels — so the eye reads it as one
       * operational scan surface.
       */}
      <CommunicationSummary
        dealId={dealId}
        deal={deal}
        events={allEvents}
        milestones={milestones}
        waitingOn={decoratedWaiting}
        participants={participants}
        lastVisit={lastVisit}
        onLogChase={logChase}
        onOpenComposer={() => setComposerOpen(true)}
        composerOpen={composerOpen}
        onRequestDoc={onRequestDoc}
        onCriticalPathResolved={onCriticalPathResolved}
        upNextLabel={upNextLabel}
        upNextStepId={upNextStepId}
        onViewStage={onViewStage}
        loading={activityStatus === "loading"}
        readOnly={variant === "phone"}
      />

      {keyDates}

      {/* Deal Activity — one cohesive card: heading + count, Log button,
       * filters, composer (collapsed until opened) and the timeline. */}
      <section
        className="rounded-2xl bg-white ring-1 ring-slate-200/70 px-3 sm:px-5 pt-4 pb-4 flex flex-col gap-4 min-w-0"
        data-testid="deal-activity-card"
        aria-labelledby="deal-activity-title"
      >
      <div className="flex items-center justify-between gap-3" data-testid="deal-activity-heading">
        <h3 id="deal-activity-title" className="text-[12px] uppercase tracking-[0.14em] font-bold text-slate-500 inline-flex items-center gap-2">
          Deal Activity
        </h3>
        <button
          type="button"
          onClick={openLogActivity}
          className="inline-flex items-center gap-1.5 min-h-[44px] lg:min-h-0 lg:h-8 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[12.5px] font-semibold cf-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 focus-visible:ring-offset-1"
          aria-expanded={composerOpen}
          data-testid="deal-activity-log-btn"
        >
          <Plus size={13} strokeWidth={2.6} /> Log activity
        </button>
      </div>

      {/* Filter bar + System activity toggle */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div
        className="flex items-center gap-1.5 -mx-1 px-1 overflow-x-auto min-w-0"
        data-testid="activity-filter-bar"
      >
        {FILTER_TABS.map((t) => {
          const active = filter === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setFilter(t.id)}
              className={`inline-flex items-center gap-1 min-h-[44px] lg:min-h-0 lg:h-8 px-3 rounded-full text-[12px] font-semibold whitespace-nowrap shrink-0 transition-colors cf-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 ${
                active
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid={`activity-filter-${t.id}`}
              aria-pressed={active}
            >
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>
      {filter !== "communication" && (
        <div className="inline-flex items-center gap-2 min-h-[44px] lg:min-h-0 shrink-0 text-[12px] text-slate-500">
          <label htmlFor={`activity-system-${dealId}`} className="cursor-pointer" data-testid="activity-system-toggle-label">System activity</label>
          <Switch
            id={`activity-system-${dealId}`}
            checked={showSystem}
            onCheckedChange={toggleSystem}
            aria-label="Show system activity"
            data-testid="activity-system-toggle"
          />
        </div>
      )}
      </div>

      {/* Composer */}
      {composerOpen && (
        <ActivityComposer
          key={composerKey}
          dealId={dealId}
          participants={participants}
          initialType={composerTypeLocal || composerInitialType}
          onCancel={closeComposer}
          onSave={(entry) => { addManual(entry); closeComposer(); }}
        />
      )}

      {/* iter-alignment · P · Deal Room · Sticky Action Bar
          Retirement (2026-03-02) — compact secondary row directly
          beneath the Log Activity composer / input.  Send message
          and Create task are the ONLY affordances that lived on
          the retired fixed bottom bar; they open the exact same
          sheets they always did (`message` / `task`) via the
          `onOpenSheet` bus the parent already owns.  Only renders
          when the parent wires the handler — permission checks
          still live upstream. */}
      {onOpenSheet && (
        <div
          className="flex items-center gap-2"
          data-testid="canonical-activity-secondary-actions"
        >
          <button
            type="button"
            onClick={() => onOpenSheet("message")}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg ring-1 ring-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 cf-press"
            title="Start an outgoing message"
            aria-label="Send message · start an outgoing message"
            data-testid="canonical-action-send-message"
          >
            <MessageSquare size={13} strokeWidth={2.3} />
            Send message
          </button>
          <button
            type="button"
            onClick={() => onOpenSheet("task")}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg ring-1 ring-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 cf-press"
            title="Open the task form"
            aria-label="Create task · open the task form"
            data-testid="canonical-action-create-task"
          >
            <ClipboardList size={13} strokeWidth={2.3} />
            Create task
          </button>
        </div>
      )}

      {/* Timeline — loading / failed states never read as "empty". */}
      {activityStatus === "error" && (
        <div
          className="rounded-xl bg-amber-50/70 ring-1 ring-amber-200/70 px-4 py-3 flex flex-wrap items-center gap-3"
          role="alert"
          data-testid="activity-error"
        >
          <AlertOctagon size={15} className="text-amber-700 shrink-0" aria-hidden />
          <div className="flex-1 min-w-[180px]">
            <div className="text-[12.5px] font-semibold text-amber-900">Activity couldn&apos;t be loaded</div>
            <div className="text-[11.5px] text-amber-800/90">
              Some entries may be missing below. Nothing has been lost — try again.
            </div>
          </div>
          {onRetryActivity && (
            <button
              type="button"
              onClick={() => onRetryActivity()}
              className="inline-flex items-center gap-1.5 min-h-[44px] lg:min-h-0 lg:h-8 px-3 rounded-lg bg-white ring-1 ring-amber-300 text-[12px] font-semibold text-amber-900 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
              data-testid="activity-error-retry"
            >
              <RefreshCw size={12} /> Retry
            </button>
          )}
        </div>
      )}
      {activityStatus === "loading" && totalCount === 0 ? (
        <ul className="flex flex-col gap-2" aria-busy="true" aria-label="Loading activity" data-testid="activity-loading">
          {[0, 1, 2].map((i) => (
            <li key={i} className="flex items-start gap-3 px-3.5 py-2.5 animate-pulse">
              <span className="w-8 h-8 rounded-lg bg-slate-100" />
              <span className="flex-1 flex flex-col gap-1.5 pt-1">
                <span className="h-3 w-2/5 rounded bg-slate-100" />
                <span className="h-2.5 w-3/5 rounded bg-slate-100" />
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {!(activityStatus !== "ready" && totalCount === 0) && (<>
      {totalCount === 0 && !composerOpen && filter !== "communication" ? (
        <div
          className="rounded-2xl bg-slate-50/60 ring-1 ring-slate-200/60 px-6 py-10 text-center"
          data-testid="activity-empty"
        >
          <StickyNote className="mx-auto mb-3 text-slate-400" size={20} />
          <div className="text-[13px] font-semibold text-slate-700">No updates yet</div>
          <div className="mt-1 text-[12px] text-slate-500 max-w-md mx-auto">
            Every email, WhatsApp, phone call, document, milestone and
            internal note logged against this transaction will appear here in
            one chronological stream.
          </div>
        </div>
      ) : filter === "communication" ? (
        /* iter-alignment · P0 · Communication Refinement (2026-02-28).
           Dedicated section that renders channel pills + purpose-built
           rows for the Communication filter only.  Every other filter
           keeps the original grouped/clustered timeline below. */
        <CommunicationBody
          events={filtered}
          channel={communicationChannel}
          onSelectChannel={setCommunicationChannel}
          onOpen={(e) => setInspected(e)}
          unavailable={activityStatus !== "ready"}
        />
      ) : (
        <div className="flex flex-col gap-6" data-testid="activity-timeline">
          {visibleRowCount === 0 && (
            <div className="rounded-xl bg-slate-50/60 ring-1 ring-slate-200/60 px-5 py-6 text-center text-[12.5px] text-slate-500" data-testid="activity-only-system">
              Nothing to show for this filter yet.{!showSystem && " Turn on System activity to see automated entries."}
            </div>
          )}
          {timeline.map((day) => (
            <section key={day.key} data-testid={`activity-day-${day.key}`}>
              <header className="mb-1 flex items-center gap-3">
                <span className="text-[12px] uppercase tracking-[0.12em] font-semibold text-slate-500" data-testid={`activity-day-label-${day.key}`}>
                  {ukDayLabel(day.key)}
                </span>
                <span className="flex-1 h-px bg-slate-200/70" aria-hidden />
              </header>
              <ul className="flex flex-col" data-testid={`activity-day-${day.key}-list`}>
                {day.rows.map((row) => (
                  row.__group ? (
                    <ActivityGroupRow key={row.id} group={row} kinds={ACTIVITY_KINDS} onInspect={(e) => setInspected(e)} />
                  ) : (
                    <ActivityRow
                      key={row.id}
                      event={row}
                      selected={inspected?.id === row.id}
                      onDelete={() => removeEvent(row.id)}
                      onInspect={() => setInspected(row)}
                      onChase={async (target) => {
                        return await logChase({
                          participantName: target?.participantName || row.participantName || null,
                          participantId: row.participantId || null,
                          roleLabel: target?.roleLabel || row.raw?.roleLabel || null,
                          subject: row.title,
                          channel: "manual",
                          body: `Chase follow-up on: ${row.title}`,
                        });
                      }}
                    />
                  )
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
      {activityHasMore && onLoadEarlier && activityStatus === "ready" && (
        <div className="flex justify-center pt-1">
          <button
            type="button"
            onClick={onLoadEarlier}
            disabled={loadingEarlier}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg text-[13px] font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
            data-testid="activity-load-earlier"
          >
            {loadingEarlier ? <><RefreshCw size={13} className="animate-spin" /> Loading…</> : <>Load earlier activity <ChevronDown size={14} /></>}
          </button>
        </div>
      )}
      </>)}

      </section>

      {/* iter-356 — Inspector Drawer overlay.
       *
       * Renders as a right-anchored drawer when an event is
       * inspected. Prioritises Message → Impact → Next Action →
       * Properties → Attachments → History so the operator can
       * scan "what happened · why it matters · what next" in one
       * pass. Non-modal — the timeline stays visible behind. */}
      {/* Portalled so the fixed drawer is viewport-anchored even inside animated (transformed) page wrappers. */}
      {inspected && createPortal(
        <InspectorDrawer
          event={inspected}
          onClose={() => setInspected(null)}
          onLogChase={logChase}
          source={resolveSource(inspected)}
        />,
        document.body,
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Composer — iter-alignment · P0 · Log Activity (approved pilot v.)
   (2026-02-28)
   ═══════════════════════════════════════════════════════════════════
   Refactor:
     • Title "Add update" → "LOG ACTIVITY".
     • Activity type selector: Note · Call · Email · WhatsApp · SMS.
       Each type maps directly onto the existing ACTIVITY_KINDS
       registry (source → kind → category), so on-save each entry
       automatically lands in the correct filter (Notes for Note,
       Communication for Call / Email / WhatsApp / SMS).  No new
       aggregator branch, no duplicate state.
     • Participant is optional for Notes; still available on every
       communication type.
     • CTA copy is contextual per type: Save note · Log call ·
       Log email · Log WhatsApp · Log SMS.
     • Timestamping is unchanged — the existing `atMs: Date.now()`
       stamp keeps every entry in the timeline order.
     • Attach file (label unchanged) still routes through the same
       attachment plumbing. */
const LOG_TYPES = [
  { id: "note",     label: "Note",     source: "note",    ctaLabel: "Save note",   Icon: StickyNote,    logTitle: "Note added" },
  { id: "call",     label: "Call",     source: "phone",   ctaLabel: "Log call",    Icon: Phone,         logTitle: "Call logged" },
  { id: "email",    label: "Email",    source: "email",   ctaLabel: "Log email",   Icon: Mail,          logTitle: "Email logged" },
  { id: "whatsapp", label: "WhatsApp", source: "message", ctaLabel: "Log WhatsApp", Icon: MessageCircle, logTitle: "WhatsApp logged" },
  { id: "sms",      label: "SMS",      source: "sms",     ctaLabel: "Log SMS",     Icon: MessageSquare, logTitle: "SMS logged" },
];

function ActivityComposer({ dealId, participants, onSave, onCancel, initialType = null }) {
  const [logType, setLogType] = useState(() => (LOG_TYPES.some((t) => t.id === initialType) ? initialType : "note"));
  const [body, setBody] = useState("");
  const [participantId, setParticipantId] = useState("");
  const [attachments, setAttachments] = useState([]);
  const textareaRef = useRef(null);
  const attachInputRef = useRef(null);

  useEffect(() => { textareaRef.current?.focus(); }, []);

  const activeType = LOG_TYPES.find((t) => t.id === logType) || LOG_TYPES[0];
  const isNote = logType === "note";
  const canSave = body.trim().length > 0;

  const handleAttach = (files) => {
    const list = Array.from(files || []).map((f) => ({ name: f.name, size: f.size }));
    setAttachments((prev) => [...prev, ...list]);
  };

  const handleSave = () => {
    if (!canSave) return;
    const participant = participants.find((p) => (p.id || p.contact_id) === participantId);
    onSave?.({
      id: makeId(),
      dealId,
      /* `source` drives the aggregator's kind mapping (see
       * ACTIVITY_KINDS + kindId resolver in aggregateActivityEvents),
       * so each save auto-classifies into the correct filter. */
      source: activeType.source,
      title: activeType.logTitle,
      body: body.trim(),
      atMs: Date.now(),
      participantId: participantId || null,
      participantName: participant?.full_name || participant?.name || participant?.email || null,
      attachments,
    });
  };

  return (
    <div
      className="rounded-2xl bg-white ring-1 ring-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_18px_36px_-24px_rgba(15,23,42,0.10)] px-5 py-4"
      data-testid="activity-composer"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-[12px] uppercase tracking-[0.10em] font-semibold text-slate-500">
          Log Activity
        </div>
        <button
          type="button" onClick={onCancel}
          className="text-slate-400 hover:text-slate-700 h-11 w-11 lg:h-7 lg:w-7 -mr-2 rounded-md flex items-center justify-center"
          aria-label="Cancel"
          data-testid="activity-composer-close"
        ><X size={14} /></button>
      </div>

      {/* Activity type selector */}
      <div className="mt-3">
        <div className="text-[11px] font-medium text-slate-500 mb-1.5">Type</div>
        <div
          className="flex flex-wrap items-center gap-1.5"
          role="tablist"
          data-testid="activity-composer-type-selector"
        >
          {LOG_TYPES.map((t) => {
            const active = t.id === logType;
            const TypeIcon = t.Icon;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-pressed={active}
                onClick={() => setLogType(t.id)}
                className={`inline-flex items-center gap-1.5 min-h-[44px] lg:min-h-0 lg:h-9 px-3 rounded-lg text-[12.5px] font-semibold transition-colors cf-press border ${
                  active
                    ? "border-violet-300 bg-violet-50 text-violet-700"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`activity-composer-type-${t.id}`}
              >
                <TypeIcon size={13} strokeWidth={2.2} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {participants && participants.length > 0 && (
        <label className="mt-3 flex flex-col gap-1">
          <span className="text-[11px] font-medium text-slate-500">
            Participant
            {isNote && <span className="text-slate-400 font-normal"> (optional for notes)</span>}
          </span>
          <select
            value={participantId}
            onChange={(e) => setParticipantId(e.target.value)}
            className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-[13px] text-slate-800 outline-none focus:ring-2 focus:ring-violet-200"
            data-testid="activity-composer-participant"
          >
            <option value="">{isNote ? "Select participant…" : "— No participant —"}</option>
            {participants.map((p) => {
              const id = p.id || p.contact_id;
              const name = p.full_name || p.name || p.email || "Unnamed";
              return <option key={id} value={id}>{name}</option>;
            })}
          </select>
        </label>
      )}

      <label className="mt-3 flex flex-col gap-1">
        <span className="text-[11px] font-medium text-slate-500">Details</span>
        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add details of your update…"
          rows={3}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:ring-2 focus:ring-violet-200 leading-[1.5] resize-y min-h-[80px]"
          data-testid="activity-composer-body"
        />
      </label>

      {attachments.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="activity-composer-attachments">
          {attachments.map((a, i) => (
            <li key={i} className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-slate-100 text-slate-600 text-[11px]">
              <Paperclip size={10} /> {a.name}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {/* iter-alignment · P0 · Deal Room · Documents
           * Consolidation (2026-03-01) — Generic document upload
           * removed from the Log Activity composer.  Documents
           * flow through the right-rail Documents card (Upload
           * · Request · View all), which auto-generates the
           * matching Activity feed rows.  Keeping this slot as
           * a visual spacer so the send button stays right-aligned
           * without a layout shift. */}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button" onClick={onCancel}
            className="min-h-[44px] lg:min-h-0 lg:h-9 px-3 rounded-lg text-[12.5px] font-semibold text-slate-600 hover:bg-slate-100"
            data-testid="activity-composer-cancel"
          >Cancel</button>
          <button
            type="button" onClick={handleSave} disabled={!canSave}
            className="min-h-[44px] lg:min-h-0 lg:h-9 px-4 rounded-lg text-[12.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="activity-composer-send"
          >{activeType.ctaLabel}</button>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   Cluster row — iter-395
   ═══════════════════════════════════════════════════════════════════
   Compact single row that stands in for a run of consecutive chase
   events sharing the same subject.  Reads as "5 chases · Signed
   contracts · latest 2h ago".  Click the caret to expand — every
   individual ActivityRow renders inline so the full audit trail
   stays visible without scrolling past a dozen near-identical
   entries.  Emails, calls, notes, docs and milestones never enter
   this row — see the `chase`-only cluster reducer above. */
/* ═══════════════════════════════════════════════════════════════════
   Row — iter-356 compact operational row
   ═══════════════════════════════════════════════════════════════════
   Each row is a scan-first operational log entry, not a chat
   bubble. Compact height, single-line title, one-line body,
   quiet timestamp column, hover reveals View · Chase · Add Note
   quick actions, click opens the Inspector Drawer.
*/
function stateChipFor(event) {
  return event.kind === "internal_note" || event.meta?.narrative
    ? { label: "INFO",     tone: "bg-sky-50 text-sky-700 ring-sky-200/70" }
    : event.kind === "milestone" || event.source === "documents" || event.kind === "system" || event.loggedBy
      ? { label: "RESOLVED", tone: "bg-emerald-50 text-emerald-700 ring-emerald-200/70" }
      : { label: "WAITING",  tone: "bg-amber-50 text-amber-700 ring-amber-200/70" };
}

function ActivityRow({ event, onDelete, onInspect, onChase, selected = false }) {
  const kind = ACTIVITY_KINDS[event.kind] || ACTIVITY_KINDS.note;
  const Icon = kind.Icon;
  // Only the WAITING chip renders; INFO / RESOLVED stay implied by icon + title.
  const stateChip = stateChipFor(event);
  const showStateChip = stateChip.label === "WAITING";
  const title = humanizeRoleKeys(event.title);
  const meta = isMilestoneEvent(event) ? recordedByLine(event) : contextLine(event, actorFor(event), title);
  const body = humanizeRoleKeys(event.body);
  return (
    <li
      className={
        "group relative border-t border-slate-100 first:border-t-0 px-2 sm:px-3.5 py-3.5 transition-colors cursor-pointer rounded-lg " +
        (selected ? "bg-violet-50/60 ring-1 ring-inset ring-violet-200/70" : "hover:bg-slate-50/60")
      }
      aria-current={selected ? "true" : undefined}
      data-testid={`activity-row-${event.id}`}
      data-selected={selected ? "true" : undefined}
      data-kind={event.kind}
      data-source={event.source}
      onClick={(e) => {
        if (e.target.closest("button, a, [data-no-inspect]")) return;
        onInspect?.(event);
      }}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className={`shrink-0 w-10 h-10 rounded-xl ring-1 ${kind.ring} ${kind.bg} flex items-center justify-center ${event.superseded ? "opacity-40 grayscale" : ""}`}>
          <Icon size={17} className={kind.tint} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => onInspect?.(event)}
              className={`text-left text-[14.5px] font-semibold leading-snug break-words focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 rounded ${event.superseded ? "text-slate-400 line-through decoration-slate-300" : "text-slate-900"}`}
              data-testid={`activity-row-title-${event.id}`}
            >
              {title}
            </button>
            {event.meta?.raw?.simulated_demo && <DemoBadge testid={`activity-row-demo-badge-${event.id}`} />}
            {event.meta?.raw?.planned && event.meta?.raw?.follow_up_on && (
              <DemoBadge
                testid={`activity-row-planned-badge-${event.id}`}
                label={`Planned · ${new Date(`${event.meta.raw.follow_up_on}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`}
                title="Planned follow-up — not yet done"
              />
            )}
            {event.superseded && (
              <span className="text-[11px] font-medium text-slate-500" data-testid={`activity-row-superseded-${event.id}`}>
                No longer current — milestone reopened
              </span>
            )}
          </div>
          {meta && (
            <div className="mt-0.5 text-[13px] text-slate-500 leading-snug break-words" data-testid={`activity-row-meta-${event.id}`}>
              {meta}
            </div>
          )}
          {body && body !== title && (
            <div
              className="mt-0.5 text-[13px] text-slate-600 leading-snug whitespace-pre-wrap break-words line-clamp-2"
              data-testid={`activity-row-body-${event.id}`}
            >
              {body}
            </div>
          )}
        </div>
        <div className="shrink-0 flex items-start gap-1.5 pt-0.5">
          {showStateChip && (
            <span
              className={`hidden sm:inline-flex items-center h-[18px] px-1.5 rounded-md ring-1 text-[9.5px] font-semibold uppercase tracking-[0.05em] ${stateChip.tone}`}
              data-testid={`activity-row-state-${event.id}`}
            >
              {stateChip.label}
            </span>
          )}
          <div className="hidden lg:flex opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity items-center gap-0.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onInspect?.(event); }}
              className="text-slate-500 hover:text-violet-700 h-7 w-7 rounded-md hover:bg-slate-50 flex items-center justify-center"
              title="View"
              data-testid={`activity-row-view-${event.id}`}
            ><Eye size={14} /></button>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                if (onChase) {
                  const entry = await onChase({
                    participantName: event.participantName || null,
                    roleLabel: event.raw?.roleLabel || null,
                  });
                  if (entry) toast.success(`Chase logged for ${entry.participantName || entry.roleLabel || "counterparty"}`);
                }
              }}
              className="text-slate-500 hover:text-violet-700 h-7 w-7 rounded-md hover:bg-slate-50 flex items-center justify-center"
              title="Chase"
              data-testid={`activity-row-chase-${event.id}`}
            ><PhoneCall size={14} /></button>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="text-slate-400 hover:text-slate-800 h-7 w-7 rounded-md hover:bg-slate-50 flex items-center justify-center"
                  aria-label="Row actions"
                  data-testid={`activity-row-menu-${event.id}`}
                ><MoreHorizontal size={14} /></button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-40 p-1.5">
                <button
                  type="button" onClick={onDelete}
                  className="w-full text-left text-[12.5px] px-2.5 py-1.5 rounded-md hover:bg-rose-50 text-rose-600"
                  data-testid={`activity-row-delete-${event.id}`}
                >Delete entry</button>
              </PopoverContent>
            </Popover>
          </div>
          <span
            className={`w-11 text-right text-[12.5px] text-slate-500 tabular-nums ${isMilestoneEvent(event) ? "invisible" : ""}`}
            data-testid={`activity-row-timestamp-${event.id}`}
            title={new Date(event.atMs).toLocaleString("en-GB", { timeZone: "Europe/London" })}
          >
            {ukTime(event.atMs)}
          </span>
          <span className="w-[18px] shrink-0" aria-hidden />
        </div>
      </div>
    </li>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   iter-356 — Communication Summary
   ═══════════════════════════════════════════════════════════════════
   One continuous surface at the top of the Communication tab
   answering "what changed · what's waiting · log an update" in a
   single scan. Not three separate cards — one composed strip.
*/
/* iter-379 · Transaction Summary.
 *
 * Refined from the iter-356/357 CommunicationSummary into a
 * structured, SSOT-driven summary that always follows the same
 * four-line format so an estate agent returning after hours or
 * days can read the state of the transaction in under 30 seconds:
 *
 *   1. CURRENT STAGE     — where the transaction is today
 *   2. PROGRESS          — the most important completed milestones
 *   3. OUTSTANDING       — what is preventing progression
 *   4. EXPECTED NEXT     — what is most likely to happen next
 *
 * "Since your last visit" + "Latest meaningful progress" are
 * INTEGRATED into this summary (as a compact footer strip) rather
 * than rendered as separate cards. Everything is derived from
 * `deal`, `events`, `milestones`, `waitingOn` — no AI-generated
 * paragraphs. The historical `communication-summary*` testids are
 * preserved so QA + audit-log wiring stay stable. */

const _STAGE_LABEL = {
  offer:            "Offer agreed",
  aml:              "AML in progress",
  memo:             "Memorandum of sale",
  solicitors:       "Solicitors instructed",
  mortgage_app:     "Mortgage application",
  mortgage:         "Mortgage in progress",
  mortgage_offer:   "Mortgage offer issued",
  survey:           "Survey in progress",
  searches:         "Searches underway",
  contract:         "Contract pack under review",
  contracts:        "Contract pack under review",
  enquiries:        "Enquiries in progress",
  exchange_ready:   "Ready to exchange",
  exchange:         "Awaiting exchange",
  exchanged:        "Contracts exchanged",
  completion:       "Completion scheduled",
  completed:        "Transaction complete",
};

const _EXPECTED_NEXT = {
  offer:            "Progress to AML + memorandum",
  aml:              "Issue memorandum of sale",
  memo:             "Instruct solicitors",
  solicitors:       "Kick off mortgage + contract pack",
  mortgage_app:     "Mortgage valuation booked",
  mortgage:         "Mortgage offer expected",
  mortgage_offer:   "Move to contract pack review",
  survey:           "Survey report expected",
  searches:         "Local authority search returns",
  contract:         "Signed contract from seller",
  contracts:        "Signed contract from seller",
  enquiries:        "Enquiries returned by seller",
  exchange_ready:   "Exchange contracts",
  exchange:         "Deposit + exchange contracts",
  exchanged:        "Completion funds movement",
  completion:       "Complete the transaction",
  completed:        "Post-completion housekeeping",
};

const _NEXT_MILESTONE = {
  offer:            "AML checks",
  aml:              "Memorandum of sale",
  memo:             "Solicitors instructed",
  solicitors:       "Mortgage application",
  mortgage_app:     "Mortgage offer",
  mortgage:         "Mortgage offer",
  mortgage_offer:   "Contract pack review",
  survey:           "Enquiries",
  searches:         "Enquiries",
  contract:         "Enquiries returned",
  contracts:        "Enquiries returned",
  enquiries:        "Exchange readiness",
  exchange_ready:   "Exchange of contracts",
  exchange:         "Exchange of contracts",
  exchanged:        "Completion",
  completion:       "Completion",
  completed:        "—",
};

/* iter-389 · Transaction Radar (replaces the iter-379 four-lane
 * Transaction Summary).
 *
 * A compact five-row scannable readout — Critical Path · Next
 * Best Action · Latest Meaningful Update · Open Commitments ·
 * Next Milestone — that answers "what's blocking, what next,
 * what changed" in under five seconds. All rows are SSOT-sourced
 * (no invented copy, no duplicated math).  The four-lane
 * Current Stage / Progress / Outstanding / Expected Next block
 * is retired because those signals are already carried by the
 * Journey Rail, Deal Overview card and Open Commitments section
 * — reading them here again added noise and drift risk.
 *
 * Historical testids preserved:
 *   `communication-summary`             — root (unchanged)
 *   `communication-summary-changes*`    — Recent Changes footer
 *   `communication-summary-composer*`   — bottom composer strip
 *   `transaction-summary-*`             — retained on the header
 *     for QA continuity; new `transaction-radar-*` testids added
 *     on the radar rows and `critical-path-*` on the new card.
 */
function CommunicationSummary({
  dealId, deal = null, events = [], milestones = null,
  waitingOn = [], participants = [],
  lastVisit = 0, composerOpen, onOpenComposer, onLogChase,
  /* iter-390 · Critical Path action wire-up.  See ActivityPanel
   * for the resolver contract. */
  onRequestDoc, onCriticalPathResolved, upNextLabel = null,
  upNextStepId = null, onViewStage, loading = false, readOnly = false,
}) {
  void dealId; void composerOpen; void milestones;

  const stageId = String(deal?.stage || deal?.status || "").toLowerCase();
  const isTerminal = stageId === "completed" || stageId === "fallen_through" || stageId === "banked";

  /* ── Critical Path — the single dependency preventing progression.
   * Not the *oldest* waiting item — the one whose owner sits on the
   * currently-active journey step (or, fallback, the waitingOn row
   * whose `days` is highest so the rank matches operator instinct).
   * SSOT: derives from `waitingOn` (canonical) + `stageId`. */
  const criticalPath = useMemo(() => {
    if (isTerminal) return null;
    const rows = Array.isArray(waitingOn) ? waitingOn.filter(Boolean) : [];
    if (rows.length === 0) return null;
    // Prefer the row whose `step_id` matches the active stage.
    const onActive = rows.find((r) => r.step_id && r.step_id === stageId);
    return onActive || rows[0];
  }, [waitingOn, stageId, isTerminal]);

  /* ── Next Best Action — canonical.
   *
   * iter-alignment · Deep NBA Payload / ActivityPanel Convergence
   * (2026-02-19).  Reads `deal.next_best_action.label` from the
   * backend SSOT (populated by `routes_deals._derive_next_best_action`,
   * shared with `/api/deal-command/{id}/overview` and the
   * Properties Quick View).  `criticalPath.detail` still wins
   * when a live blocker demands attention — the operator's
   * blocker resolution is the true next action for that deal.
   * `_EXPECTED_NEXT` survives ONLY as a defensive offline
   * fallback for legacy consumers that render this panel
   * without a `deal` prop.
   */
  const nextBestAction = useMemo(() => {
    const canonical = deal?.next_best_action?.label
                   || deal?.nextBestAction?.label
                   || null;
    if (isTerminal) return canonical || _EXPECTED_NEXT[stageId] || null;
    if (criticalPath?.detail) return criticalPath.detail;
    return canonical || _EXPECTED_NEXT[stageId] || "Progress the next milestone";
  }, [deal, criticalPath, stageId, isTerminal]);

  /* ── Latest Meaningful Update — first "material" progress signal. */
  const lastMeaningful = useMemo(() => {
    const materialKinds = new Set([
      "milestone", "milestone_complete", "milestone_progressed",
      "document_upload", "document_signed", "document_received",
    ]);
    for (const e of events) {
      if (!materialKinds.has(e?.kind)) continue;
      if (e?.meta?.activityType === "milestone_reopened") continue;  // a reopen is not progress
      if (e?.staleCompletion) continue;  // completion since reopened
      return e;
    }
    return null;
  }, [events]);

  /* ── Next Milestone — next applicable stage after the rail focus (parent-resolved); stage map for pre-journey. */
  const nextMilestone = isTerminal
    ? (stageId === "fallen_through" ? "—" : "None — transaction complete")
    : (loading && !milestones) ? "Loading…"
    : (upNextLabel || _NEXT_MILESTONE[stageId] || "Progress the next milestone");

  /* ── Recent Changes — deltas since the operator's last visit,
   * capped at 5.  When empty, the whole footer is hidden. */
  const recentDeltas = useMemo(() => {
    const deltas = lastVisit > 0
      ? events.filter((e) => (e.atMs || 0) > lastVisit)
      : events;
    return deltas.slice(0, 5);
  }, [events, lastVisit]);

  /* ── Deal-health pill (kept — reads as the header's calm health
   * chip; testid preserved for QA continuity). */
  const hasBlockers = (waitingOn || []).length > 0;
  // Canonical open enquiry records — an enquiry is not a blocker unless it is raised as one.
  const [openEnquiries, setOpenEnquiries] = useState(null);
  useEffect(() => {
    if (!dealId) return undefined;
    let alive = true;
    api.get(`/api/v2/deals/${dealId}/enquiries`)
      .then(({ data }) => { if (alive) setOpenEnquiries((data || []).filter((q) => q?.status !== "resolved").length); })
      .catch(() => { if (alive) setOpenEnquiries(null); });
    return () => { alive = false; };
  }, [dealId, events.length]);
  // Same canonical band as the journey pill (overdue exchange / flagged risk → At Risk).
  const healthCanon = dealStatusFor(deal || {}, openEnquiries == null ? undefined : { openEnquiries });
  const healthTone = isTerminal
    ? { bg: "bg-emerald-50", text: "text-emerald-700", label: "Complete" }
    : healthCanon.terminal === "exchanged"
      ? { bg: "bg-emerald-50", text: "text-emerald-700", label: "Exchanged" }
    : (hasBlockers || !healthCanon.onTrack)
      ? { bg: "bg-amber-50", text: "text-amber-800", label: "At Risk" }
      : { bg: "bg-emerald-50", text: "text-emerald-700", label: "On Track" };

  /* ── Ranked Critical Path metadata for the dedicated card. */
  const cpMeta = useMemo(() => {
    if (!criticalPath) return null;
    const days = Number(criticalPath.days || criticalPath.age_days || 0);
    /* Owner resolution — prefer the participant name (via the
     * `actor_id → participants[]` lookup) so the card shows a
     * human, not the blocker label.  Fall back to the row's
     * canonical labels, then to the participant role. */
    const actorId = criticalPath.actor_id || criticalPath.user_id || criticalPath.participant_id;
    const actor = actorId
      ? (participants || []).find((p) => p.user_id === actorId || p.id === actorId || p.contact_id === actorId)
      : null;
    const ownerName = actor?.full_name || actor?.email?.split("@")[0] || null;
    const ownerRole = actor?.role_label || actor?.role || criticalPath.role_label || null;
    const owner = ownerName
      ? (ownerRole ? `${ownerName} · ${ownerRole}` : ownerName)
      : (criticalPath.party || criticalPath.role_label || "Counterparty");
    const outcome = criticalPath.detail || criticalPath.sub || "Response pending";
    const impact = days >= 7
      ? "Exchange window at risk — escalate today"
      : days >= 3
        ? "Slowing momentum — chase today"
        : days >= 1
          ? "On the critical path — follow up soon"
          : "Fresh — track for response";
    // Who / what is being waited on, since when, and the recorded action owner.
    const rec = criticalPath.recipient || null;
    const roleText = _roleText(criticalPath.owner_role || criticalPath.role_label || criticalPath.party);
    const who = rec?.full_name
      ? (roleText ? `${rec.full_name} · ${roleText}` : rec.full_name)
      : (roleText || owner);
    const sinceIso = criticalPath.raised_at || criticalPath.created_at
      || (days > 0 ? new Date(Date.now() - days * 86400000).toISOString() : null);
    const since = sinceIso
      ? new Date(sinceIso).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
      : null;
    const lastChasedMs = criticalPath.last_chased_at
      ? Date.parse(criticalPath.last_chased_at)
      : (criticalPath.lastChaseAtMs || null);
    return {
      owner, outcome, days, impact, who, since, lastChasedMs,
      what: criticalPath.title || criticalPath.detail || "Response pending",
      actionOwner: ownerName ? owner : null,
    };
  }, [criticalPath, participants]);

  return (
    <section
      className="rounded-2xl bg-white ring-1 ring-slate-200/70"
      data-testid="communication-summary"
    >
      {/* Header — title + deal-health pill */}
      <header className="flex items-center gap-3 px-5 pt-4 pb-2">
        <div className="h-7 w-7 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
          <Target size={14} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[13.5px] font-semibold text-slate-900"
            data-testid="transaction-summary-title"
          >
            Transaction Radar
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 truncate" data-testid="transaction-radar-health-reason">
            {isTerminal
              ? (stageId === "fallen_through" ? "Transaction fell through" : "Transaction complete")
              : (healthCanon.reasons?.[0] || "What matters right now")}
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[10.5px] font-semibold ${healthTone.bg} ${healthTone.text}`}
          data-testid="transaction-summary-health"
          aria-label={`Deal health · ${healthTone.label}`}
          title={healthCanon.reasons?.[0] || undefined}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
          {healthTone.label}
        </span>
      </header>

      {/* Waiting On card — who / what the deal is waiting on, since when,
          last chase and the recorded action owner.  Reuses the existing
          Reply · Call · Request Document · Complete actions; Complete
          resolves the commitment only — never a milestone or stage. */}
      {cpMeta && (
        <div
          className={`mx-5 mb-2 mt-1 rounded-xl ring-1 p-4 ${criticalPath.severity === "blocking" ? "ring-rose-200/70 bg-rose-50/40" : "ring-slate-200/80 bg-slate-50/50"}`}
          data-testid="critical-path-card"
        >
          <div className="flex items-start gap-2.5">
            <div className={`h-6 w-6 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${criticalPath.severity === "blocking" ? "bg-rose-100 text-rose-600" : "bg-slate-200/70 text-slate-600"}`}>
              <Hourglass size={13} strokeWidth={2.4} />
            </div>
            <div className="min-w-0 flex-1" data-testid="transaction-radar-critical-path">
              <div
                className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500"
              >
                Waiting On
                <span className="normal-case tracking-normal font-medium text-slate-400" data-testid="critical-path-source"> · open blocker</span>
              </div>
              <div
                className="text-[13.5px] font-bold text-slate-900 leading-snug mt-0.5 truncate"
                data-testid="critical-path-title"
              >
                {cpMeta.who}
              </div>
              <p
                className="mt-0.5 text-[12px] text-slate-700 leading-snug"
                data-testid="critical-path-reason"
              >
                {cpMeta.what}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500 tabular-nums" data-testid="critical-path-timing">
                <span data-testid="critical-path-since">
                  {cpMeta.since
                    ? `Since ${cpMeta.since} · ${cpMeta.days} ${cpMeta.days === 1 ? "day" : "days"}`
                    : "Just raised"}
                </span>
                <span data-testid="critical-path-last-chased">
                  {cpMeta.lastChasedMs ? `Last chased ${relative(cpMeta.lastChasedMs)}` : "Not chased yet"}
                </span>
                {cpMeta.actionOwner && (
                  <span data-testid="critical-path-action-owner">Action owner: {cpMeta.actionOwner}</span>
                )}
              </div>
              <dl hidden aria-hidden="true" className="hidden mt-2 grid-cols-2 gap-x-4 gap-y-1.5 text-[11.5px] leading-snug">
                <div>
                  <dt className="text-slate-400 font-medium uppercase tracking-[0.08em] text-[9.5px]">Owner</dt>
                  <dd className="text-slate-800 font-semibold truncate" data-testid="critical-path-owner">
                    {cpMeta.owner}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-400 font-medium uppercase tracking-[0.08em] text-[9.5px]">Waiting</dt>
                  <dd className="text-slate-800 font-semibold" data-testid="critical-path-waiting">
                    {cpMeta.days > 0
                      ? `${cpMeta.days} ${cpMeta.days === 1 ? "day" : "days"}`
                      : "Just raised"}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-400 font-medium uppercase tracking-[0.08em] text-[9.5px]">Expected Outcome</dt>
                  <dd className="text-slate-700 truncate" data-testid="critical-path-outcome">{cpMeta.outcome}</dd>
                </div>
                <div>
                  <dt className="text-slate-400 font-medium uppercase tracking-[0.08em] text-[9.5px]">Estimated Impact</dt>
                  <dd className="text-slate-700 truncate" data-testid="critical-path-impact">{cpMeta.impact}</dd>
                </div>
              </dl>
              {!readOnly && <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="critical-path-actions">
                <CriticalPathAction
                  icon={<Mail size={11} />}
                  label="Reply"
                  onClick={onOpenComposer}
                  testid="critical-path-action-reply"
                />
                <CriticalPathAction
                  icon={<Phone size={11} />}
                  label="Call"
                  onClick={() => {
                    /* iter-390 · Call action.  Opens the same
                     * composer used for Reply — the composer's
                     * channel selector then routes to the
                     * existing call-log flow with the critical
                     * path pre-selected as target. */
                    if (typeof onLogChase === "function") {
                      onLogChase({ channel: "call", target: criticalPath, note: null });
                    }
                    onOpenComposer?.();
                  }}
                  testid="critical-path-action-call"
                />
                <CriticalPathAction
                  icon={<FilePlus2 size={11} />}
                  label="Request Document"
                  onClick={() => {
                    /* iter-390 · Reuse the existing request-doc
                     * sheet from the right rail — no parallel
                     * flow, no duplicate audit entries. */
                    if (typeof onRequestDoc === "function") {
                      onRequestDoc(criticalPath);
                    } else {
                      /* Fallback for the standalone / preview
                       * mount where the parent didn't wire the
                       * request-doc sheet. */
                      onOpenComposer?.();
                    }
                  }}
                  testid="critical-path-action-request-doc"
                />
                <CriticalPathAction
                  icon={<Check size={11} />}
                  label="Complete"
                  primary
                  onClick={() => {
                    /* iter-390 · Complete resolves the commitment
                     * via the parent's SSOT-aware handler.  The
                     * parent emits the audit row, persists the
                     * resolution, and re-fetches so the next
                     * Critical Path is promoted automatically. */
                    onCriticalPathResolved?.(criticalPath);
                  }}
                  testid="critical-path-action-complete"
                />
              </div>}
            </div>
          </div>
        </div>
      )}

      {/* Radar rows — Waiting On (when clear) · Latest Change · Up Next */}
      <ul
        className="px-5 pb-3 pt-1 divide-y divide-slate-100"
        data-testid="transaction-radar"
      >
        {!cpMeta && (
          <RadarRow
            icon={<Hourglass size={13} strokeWidth={2.2} />}
            iconTone="text-slate-500 bg-slate-100"
            label="Waiting On"
            value={openEnquiries > 0
              ? `No open blocker · ${openEnquiries} ${openEnquiries === 1 ? "enquiry" : "enquiries"} awaiting reply`
              : "No open dependency — chain moving freely"}
            testid="transaction-radar-critical-path"
          />
        )}
        <RadarRow
          icon={<CheckCircle2 size={13} strokeWidth={2.2} />}
          iconTone="text-emerald-500 bg-emerald-50"
          label="Latest Change"
          value={lastMeaningful ? readableStageTitle(lastMeaningful) : loading ? "Loading…" : "No material update recorded yet"}
          rightMeta={lastMeaningful ? clock(lastMeaningful.atMs) : null}
          testid="transaction-radar-last-meaningful"
        />
        <RadarRow
          icon={<MilestoneIcon size={13} strokeWidth={2.2} />}
          iconTone="text-indigo-500 bg-indigo-50"
          label="Up Next"
          {...(readOnly ? { label: "Next Milestone" } : {})}
          value={nextMilestone}
          rightMeta={upNextStepId && onViewStage ? null : "Next milestone"}
          action={upNextStepId && onViewStage && !isTerminal ? (
            <button
              type="button"
              onClick={() => onViewStage(upNextStepId)}
              className="inline-flex items-center gap-1 min-h-[44px] lg:min-h-0 lg:h-7 px-2.5 rounded-md text-[11.5px] font-semibold text-violet-700 hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
              aria-label={`View stage · ${nextMilestone}`}
              data-testid="transaction-radar-view-stage"
            >
              View stage <ChevronRight size={12} />
            </button>
          ) : null}
          testid="transaction-radar-next-milestone"
        />
      </ul>

      {/* iter-alignment · P27 · De-dup pass — Recent Changes
          footer retired.  The same deltas already surface in
          the Activity Timeline (Updates tab) — the footer
          duplicated them without adding new context.  The
          `recentDeltas` computation is left intact so future
          consumers (e.g. a right-rail digest) can still read
          the SSOT-derived list. */}

      {/* Quick-action composer bar removed — "+ Log activity" is the single entry point. */}
    </section>
  );
}

/* iter-389 · Compact row inside the Transaction Radar.  Icon +
 * label + value + optional right-meta.  Keeps the radar dense so
 * five rows read in a single glance. */
function RadarRow({ icon, iconTone, label, value, rightMeta, action = null, testid }) {
  return (
    <li
      className="flex items-center gap-3 py-2"
      data-testid={testid}
    >
      <span className={`shrink-0 h-6 w-6 rounded-md flex items-center justify-center ${iconTone || "text-slate-400 bg-slate-50"}`}>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-400 leading-none">
          {label}
        </div>
        <div className="text-[12.5px] text-slate-800 mt-1 truncate leading-snug">
          {value}
        </div>
      </div>
      {rightMeta && (
        <span className="shrink-0 text-[10.5px] text-slate-400 tabular-nums">
          {rightMeta}
        </span>
      )}
      {action && <span className="shrink-0">{action}</span>}
    </li>
  );
}

/* Plain-English party label for a blocker's owner_role. */
const _ROLE_TEXT = {
  solicitor_seller: "Seller's solicitor", seller_solicitor: "Seller's solicitor",
  solicitor_buyer: "Buyer's solicitor", buyer_solicitor: "Buyer's solicitor",
  broker: "Mortgage broker", mortgage_broker: "Mortgage broker", agent: "Agent",
  buyer: "Buyer", seller: "Seller", lender: "Lender", surveyor: "Surveyor",
  managing_agent: "Managing agent", freeholder: "Freeholder",
};
function _roleText(role) {
  if (!role) return null;
  const key = String(role).toLowerCase().replace(/[\s-]+/g, "_");
  return _ROLE_TEXT[key] || String(role).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

/* iter-389 · Critical Path action button.  Compact pill; primary
 * variant carries the emerald "complete" affirmative. */
function CriticalPathAction({ icon, label, onClick, primary, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-md text-[11px] font-semibold transition ${
        primary
          ? "bg-emerald-600 text-white hover:bg-emerald-700"
          : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 hover:text-slate-900"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   iter-356 — Inspector Drawer
   ═══════════════════════════════════════════════════════════════════
   Right-anchored drawer opened when an operator clicks a
   timeline row. Prioritises the operational Q&A:
     1. Message      — full context of what happened
     2. Impact       — why it matters (delay signal etc.)
     3. Next Action  — the contextual recommended step
     4. Properties   — structured metadata (type, tags, IDs)
     5. Attachments  — files linked to this event
     6. History      — related upstream events
   Non-modal — timeline stays scannable behind.
*/
function InspectorDrawer({ event, onClose, onLogChase, source = null }) {
  const kind = ACTIVITY_KINDS[event.kind] || ACTIVITY_KINDS.note;
  const Icon = kind.Icon;
  const [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Impact + Next Action derivations. Kept calm — the drawer
  // must never invent facts. If we can't compute impact, the
  // section still renders with a neutral placeholder.
  const isBlocking = event.kind === "email" || event.kind === "document_request";
  // iter-alignment · P0 · Deal Activity Drawer V2 (2026-03-02) —
  // Impact + Next Action must only render when they add
  // operational value; the previous "always render" pattern
  // produced filler on note / call / system rows.
  const impactCopy = isBlocking
    ? { headline: "Exchange likely delayed if not chased soon.", tone: "Waiting", toneClass: "text-amber-700" }
    : event.source === "milestones"
      ? { headline: "Progression milestone marked complete.", tone: "Resolved", toneClass: "text-emerald-700" }
      : null;
  const isChaseable = isBlocking || event.kind === "message" || event.kind === "email";
  const nextActionCta = event.source === "documents"
    ? `Review "${event.meta?.docName || "document"}"`
    : isChaseable
      ? `Chase ${event.participantName || "counterparty"}`
      : null;
  // iter-357 → iter-358 · Contextual Next Action now PERSISTS to
  // the backend audit trail. Chases hit the shared `deal_activity`
  // table via `onLogChase` (async); non-chase CTAs still toast so
  // the timeline reflects only real operational writes.
  const handleNextAction = async () => {
    if (isChaseable && onLogChase) {
      const entry = await onLogChase({
        participantName: event.participantName || null,
        participantId: event.participantId || null,
        roleLabel: event.raw?.roleLabel || null,
        subject: event.title,
        channel: event.kind === "email" ? "email" : event.kind === "sms" ? "sms" : "manual",
        body: `Follow-up chase from inspector on: ${event.title}`,
      });
      if (entry) toast.success(`Chase logged · ${entry.participantName || entry.roleLabel || "counterparty"}`);
      return;
    }
    if (nextActionCta) toast.success(nextActionCta);
  };
  // iter-alignment · P0 · Deal Activity Drawer V2 (2026-03-02) —
  // Activity body is the canonical feed text.  Fall back to the
  // row title (always populated on a canonical event) so we
  // NEVER render the placeholder "No detail captured for this
  // event." string the pilot reviewers flagged.
  const activityBody = (event.body && event.body.trim()) || event.title;
  const attachmentName = event.meta?.docName || null;
  return (
    <aside
      role="dialog"
      aria-label={`Inspector · ${event.title}`}
      /* iter-alignment · P0 · Inspector Responsive Fit (2026-02-28)
       * ---------------------------------------------------------
       * Desktop appearance is unchanged (≥lg) — full-height flush
       * right edge, 420px wide.  On iPad + smaller we cap the
       * width so meaningful context of the Communication thread
       * stays visible behind the drawer, add a small top/bottom
       * inset so the header + close control aren't clipped by
       * the safe-area, round the corners so the drawer reads as
       * a floating panel, and clip any accidental overflow to
       * kill horizontal page scroll.  Body scroll is inherited
       * from the existing `flex-1 overflow-y-auto` region below.
       */
      className={
        "fixed z-40 " +
        "max-md:z-[60] " +
        "top-2 bottom-2 right-2 " +
        "lg:top-0 lg:bottom-0 lg:right-0 " +
        "w-[min(360px,calc(100vw-96px))] " +
        "md:w-[min(400px,calc(100vw-160px))] " +
        "lg:w-[420px] " +
        "bg-white ring-1 ring-slate-200/70 " +
        "rounded-2xl lg:rounded-none " +
        "shadow-[0_1px_2px_rgba(15,23,42,0.06),0_28px_60px_-20px_rgba(15,23,42,0.28)] " +
        "flex flex-col overflow-hidden"
      }
      data-testid="communication-inspector"
    >
      <header className="flex items-start justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`shrink-0 w-10 h-10 rounded-xl ring-1 ${kind.ring} ${kind.bg} flex items-center justify-center`}>
            <Icon size={18} className={kind.tint} />
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-bold text-slate-900 leading-tight break-words">{humanizeRoleKeys(event.title)}</div>
            {stateChipFor(event).label === "WAITING" && (
              <div className="mt-1 inline-flex items-center h-[18px] px-1.5 rounded-md ring-1 bg-amber-50 text-amber-700 ring-amber-200/70 text-[9.5px] font-semibold uppercase tracking-[0.05em]" data-testid="communication-inspector-state">
                WAITING
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 h-8 w-8 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800 flex items-center justify-center"
          aria-label="Close inspector"
          data-testid="communication-inspector-close"
        ><X size={16} /></button>
      </header>

      {/* Meta line: date · participant · role */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap text-[11.5px] text-slate-500">
        <span className="inline-flex items-center gap-1"><ClockIcon size={12} /> {ukDayLabel(ukDayKey(event.atMs))}, {ukTime(event.atMs)}</span>
        {actorFor(event) && <><span className="text-slate-300">·</span><span className="font-medium text-slate-700" data-testid="communication-inspector-actor">{isMilestoneEvent(event) ? `Recorded by ${actorFor(event)}` : actorFor(event)}</span></>}
      </div>

      {/* Tabs retired (iter-alignment · P0 · Deal Activity Drawer
          V2, 2026-03-02) — every section now renders inline so
          Title / actor·time / Activity / Impact / Next Action sit
          above the fold on desktop + iPad, then Attachments /
          History / Details flow underneath.  Drawer body stays
          `overflow-y-auto` so it scrolls independently while the
          underlying Deal Room keeps its scroll position. */}

      {/* Body — scrollable */}
      <div
        className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5"
        data-testid="communication-inspector-body"
      >
        {/* 1 · Activity (canonical feed text) */}
        <section data-testid="communication-inspector-message">
          <div className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500 mb-1.5">Activity</div>
          <div
            className="rounded-lg ring-1 ring-slate-200/70 bg-white px-3 py-2.5 text-[13px] text-slate-800 leading-[1.55] whitespace-pre-wrap"
            data-testid="communication-inspector-activity-body"
          >
            {humanizeRoleKeys(activityBody)}
          </div>
        </section>

        {source && (
          <section data-testid="communication-inspector-source">
            {source.unavailable ? (
              <div className="text-[12px] text-slate-500" data-testid="communication-inspector-source-unavailable">{source.unavailable}</div>
            ) : (
              <button
                type="button"
                onClick={() => { source.onOpen(); onClose?.(); }}
                className="inline-flex items-center gap-1.5 min-h-[44px] lg:min-h-0 lg:h-8 px-3 rounded-lg ring-1 ring-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
                data-testid="communication-inspector-source-link"
              >
                {source.label} <ChevronRight size={13} />
              </button>
            )}
          </section>
        )}

        {/* 2 · Impact — hidden when no operational Impact applies. */}
        {impactCopy && (
          <section data-testid="communication-inspector-impact">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500">Impact</div>
              <span className={`text-[10.5px] font-semibold ${impactCopy.toneClass}`}>{impactCopy.tone}</span>
            </div>
            <div className="rounded-lg ring-1 ring-slate-200/70 bg-slate-50/70 px-3 py-2.5 text-[12.5px] text-slate-700 leading-[1.55]">
              {impactCopy.headline}
            </div>
          </section>
        )}

        {/* 3 · Next Action — hidden when no chaseable / reviewable CTA. */}
        {nextActionCta && (
          <section data-testid="communication-inspector-next-action">
            <div className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500 mb-1.5">Next action</div>
            <button
              type="button"
              onClick={handleNextAction}
              className="w-full text-left rounded-lg bg-violet-50 hover:bg-violet-100 ring-1 ring-violet-200/70 px-3 py-2.5 text-[12.5px] font-semibold text-violet-800 flex items-center justify-between transition"
              data-testid="communication-inspector-next-action-cta"
            >
              <span className="inline-flex items-center gap-2"><PhoneCall size={13} /> {nextActionCta}</span>
              <ChevronRight size={14} className="text-violet-500" />
            </button>
          </section>
        )}

        {/* 4 · Attachments — compact row when none. */}
        <section data-testid="communication-inspector-attachments">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500">Attachments</div>
            {!attachmentName && <span className="text-[10.5px] text-slate-400 tabular-nums">0</span>}
          </div>
          {attachmentName ? (
            <div className="rounded-lg ring-1 ring-slate-200/70 px-3 py-2.5 flex items-center gap-2">
              <FileText size={14} className="text-indigo-600" />
              <span className="text-[12.5px] text-slate-800 truncate">{attachmentName}</span>
            </div>
          ) : (
            <div className="text-[11.5px] text-slate-400">No attachments.</div>
          )}
        </section>

        {/* 5 · History — collapsed by default, expandable inline. */}
        <section data-testid="communication-inspector-history">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="w-full flex items-center justify-between text-left"
            aria-expanded={historyOpen}
            data-testid="communication-inspector-history-toggle"
          >
            <span className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500">View history</span>
            <ChevronRight
              size={14}
              className={`text-slate-400 transition-transform ${historyOpen ? "rotate-90" : ""}`}
            />
          </button>
          {historyOpen && (
            <div className="mt-1.5 text-[11.5px] text-slate-500 leading-[1.55]">
              Related upstream events will appear here as the timeline grows.
            </div>
          )}
        </section>

        {/* 6 · Details — compact grid at the bottom. */}
        <section data-testid="communication-inspector-properties">
          <div className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500 mb-1.5">Details</div>
          <dl className="grid grid-cols-2 gap-y-1 gap-x-4 text-[11.5px]">
            <dt className="text-slate-500">Type</dt>
            <dd className="text-slate-800 font-medium first-letter:uppercase">{event.meta?.activityType ? humanizeRoleKeys(event.meta.raw?.log_kind || event.meta.activityType) : kind.label}</dd>
            <dt className="text-slate-500">Category</dt>
            <dd className="text-slate-800 font-medium capitalize">{kind.category}</dd>
            <dt className="text-slate-500">Source</dt>
            <dd className="text-slate-800 font-medium capitalize">{event.source || "manual"}</dd>
          </dl>
        </section>
      </div>
    </aside>
  );
}

export default ActivityPanel;
/* Backwards-compatible alias so any import of `UpdatesPanel`
 * (iter-343 → iter-347) still resolves cleanly. */
export { ActivityPanel as UpdatesPanel };
